import React, { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react'
import { Factory, Package, RefreshCw, Loader2, ChevronRight, Home, Trash2, AlertCircle, CheckCircle2, X, Edit, Settings, Plus, PlayCircle, Square, Calculator, Send } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Card } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { api } from '../../../lib/api'
import { useLanguage } from '../../../context/LanguageContext'
import { useFactory } from '../../../context/FactoryContext'
import { useTerminal } from '../../../context/TerminalContext'
import { findLineForTerminal, buildLineFromTerminal } from '../../../utils/terminal/terminalLine'
import { sapConsumptionService } from '../../../services/sapConsumptionService'

// Importar hooks y componentes refactorizados
import {
  useNotifications,
  useMaterialsConsumptionData,
  queryKeys,
  normalizeLineContext,
  normalizeOrderNumber,
  usePullOrderRecipeFromSapMutation,
} from './MaterialesConsumos/hooks'
import { Notifications } from './MaterialesConsumos/components/shared/Notifications'
import { formatActiveOrderConflictMessage } from './MaterialesConsumos/utils/orderErrorMessages'
import {
  getActiveOrderFromList,
  sortOrdersWithActiveFirst,
  isOrderActiveInMES,
  getOrderDisplayStatus,
} from './MaterialesConsumos/utils/orderStatus'
import { cn } from '../../../lib/utils'
import { LoadingState, EmptyState } from './MaterialesConsumos/components/shared/LoadingState'
import { PageHeader } from './MaterialesConsumos/components/shared/PageHeader'
import { EditConsumptionModal } from './MaterialesConsumos/components/shared/EditConsumptionModal'
import { EditPeriodEndModal } from './MaterialesConsumos/components/shared/EditPeriodEndModal'
import { DosifierManagerModal } from './MaterialesConsumos/components/shared/DosifierManagerModal'
import { HopperManagerModal } from './MaterialesConsumos/components/shared/HopperManagerModal'
import { ComponentManagerModal } from './MaterialesConsumos/components/shared/ComponentManagerModal'
import LineCard from './MaterialesConsumos/components/LineSelector/LineCard'
import { OrderDetailHeader, OrderSummaryAndRecipe, ConsumptionPeriodsManager } from './MaterialesConsumos/components/OrderDetail'

// Helper para debugging solo en desarrollo
const isDev = import.meta.env.DEV
const debug = {
  log: isDev ? console.log.bind(console) : () => {},
  error: isDev ? console.error.bind(console) : () => {},
  warn: isDev ? console.warn.bind(console) : () => {}
}

/** El backend valida FinishedAt ≥ inicio del último período (`UpdateOrderDatesWithValidation`). */
function computeMinimumOrderFinishedAtFromPeriods(periodsSnapshot) {
  if (!Array.isArray(periodsSnapshot) || periodsSnapshot.length === 0) return null
  const last = periodsSnapshot[periodsSnapshot.length - 1]
  const lsRaw = last?.PeriodStart ?? last?.periodStart ?? last?.period_start
  if (lsRaw == null || lsRaw === '') return null
  const ls = new Date(lsRaw)
  return Number.isNaN(ls.getTime()) ? null : ls
}

// ============================================================================
// COMPONENTES MEMOIZADOS PARA OPTIMIZACIÓN
// ============================================================================
// LineCard ahora se importa desde archivos separados (optimizado)

function MaterialsConsumablesPage() {
  const { t } = useLanguage()

  // Hook de notificaciones refactorizado
  const { error, success, showError, showSuccess, clearError, clearSuccess } = useNotifications()

  const { selectedFactory } = useFactory()
  const { isHmiMode, terminal, loading: terminalLoading } = useTerminal()
  const isGlobalView = !selectedFactory

  // Silenciar errores de extensiones del navegador (no afectan la funcionalidad)
  useEffect(() => {
    const extPatterns = ['message channel closed', 'asynchronous response', 'listener indicated', 'Extension context invalidated', 'Receiving end does not exist']
    const handleUnhandledRejection = (event) => {
      const msg = event.reason?.message || String(event.reason || '')
      if (extPatterns.some(p => msg.includes(p))) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  }, [])

  // Limpiar selecciones cuando cambia la fábrica (excepto en HMI: la fábrica la fija el terminal)
  useEffect(() => {
    if (isHmiMode) return
    setSelectedLine(null)
    setSelectedOrder(null)
  }, [selectedFactory, isHmiMode])

  // ─── UI-state (selecciones del usuario) ────────────────────────────────────
  const [selectedLine, setSelectedLine] = useState(null)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [activePeriod, setActivePeriod] = useState(null)
  const [declarationTargetPeriodId, setDeclarationTargetPeriodId] = useState(null)

  // ─── Carga de datos vía TanStack Query ────────────────────────────────────
  const queryClient = useQueryClient()
  const {
    ctx: dataCtx,
    rawAssets,
    orders,
    recipe,
    periods,
    derivedActivePeriod,
    consumptions,
    declaredQuantitiesByComponent,
    rawDosingByLine,
    isLoadingLines,
    isLoadingOrders,
    isLoadingRecipe,
    isLoadingPeriods,
    isLoadingConsumptions,
    isLoadingDosifiers,
    refetchAssets,
    refetchOrders,
    refetchRecipe,
    refetchPeriods,
    refetchConsumptions,
    refetchHopperPeriods,
    refetchDosingByLine,
  } = useMaterialsConsumptionData({
    selectedLine,
    selectedOrder,
    activePeriod,
    selectedFactory,
  })

  const pullRecipeFromSapMutation = usePullOrderRecipeFromSapMutation()

  const lineActiveOrder = useMemo(() => getActiveOrderFromList(orders), [orders])
  const sortedOrders = useMemo(() => sortOrdersWithActiveFirst(orders), [orders])

  // Sincronizar activePeriod con el período activo derivado de la query de períodos
  useEffect(() => {
    if (derivedActivePeriod) {
      setActivePeriod(derivedActivePeriod)
    }
  }, [derivedActivePeriod])

  // ─── Estado de UI derivado de TanStack ────────────────────────────────────
  const loading = isLoadingLines
  const loadingOrders = isLoadingOrders
  const loadingRecipe = isLoadingRecipe
  const loadingDosifiersAPI = isLoadingDosifiers

  // Estados locales de operación para handlers que lanzan mutaciones directas
  const [_loadingPeriods, setLoadingPeriods] = useState(false)
  const [_loadingConsumptions, setLoadingConsumptions] = useState(false)
  const loadingPeriods = isLoadingPeriods || _loadingPeriods
  const loadingConsumptions = isLoadingConsumptions || _loadingConsumptions

  // ─── Estados de líneas (multi-línea view) ─────────────────────────────────
  const [linesWithData, setLinesWithData] = useState([])
  const [loadingLineData, setLoadingLineData] = useState(false)

  // ─── Otros estados de operación ───────────────────────────────────────────
  const [expandedPeriodId, setExpandedPeriodId] = useState(null)
  const [expandedPeriodConsumptions, setExpandedPeriodConsumptions] = useState({})
  const [loadingExpandedPeriod, setLoadingExpandedPeriod] = useState(null)
  const [sendingToSAP, setSendingToSAP] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [addingConsumption, setAddingConsumption] = useState(false)
  const [updatingSAP, setUpdatingSAP] = useState(false)
  const [declarationForm, setDeclarationForm] = useState({
    componentSapCode: '',
    dosifierId: '',
    hopperId: ''
  })
  
  // Estados para Inicio y Fin de OF
  const [ofStartDateTime, setOfStartDateTime] = useState(null) // Formato: Date object
  const [ofEndDateTime, setOfEndDateTime] = useState(null) // Formato: Date object
  const [showStartModal, setShowStartModal] = useState(false)
  const [showEndModal, setShowEndModal] = useState(false)
  const [tempStartDateTime, setTempStartDateTime] = useState('')
  const [tempEndDateTime, setTempEndDateTime] = useState('')
  const [savingStartDateTime, setSavingStartDateTime] = useState(false)
  const [savingEndDateTime, setSavingEndDateTime] = useState(false)
  
  // Estados para modales de gestión CRUD
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingConsumption, setEditingConsumption] = useState(null)
  const [showEditPeriodEndModal, setShowEditPeriodEndModal] = useState(false)
  const [editingPeriod, setEditingPeriod] = useState(null)
  const [savingPeriodDates, setSavingPeriodDates] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [adjustingConsumption, setAdjustingConsumption] = useState(null)
  const [showDosifierModal, setShowDosifierModal] = useState(false)
  const [showHopperModal, setShowHopperModal] = useState(false)
  const [showComponentModal, setShowComponentModal] = useState(false)
  const [dosifiers, setDosifiers] = useState([])
  const [hoppers, setHoppers] = useState([])
  const [components, setComponents] = useState([])
  const [managingCRUD, setManagingCRUD] = useState(false)

  // Limpiar activePeriod y periodo destino de declaración cuando cambia la orden
  useEffect(() => {
    if (!selectedOrder || !selectedLine) {
      setActivePeriod(null)
    }
    setDeclarationTargetPeriodId(null)
  }, [selectedOrder, selectedLine])

  // Sincronizar fechas de inicio y fin de OF con los datos de la orden seleccionada
  useEffect(() => {
    if (selectedOrder) {
      debug.log('📅 Sincronizando fechas de OF con orden seleccionada:', {
        orderNumber: selectedOrder.OrderNumber,
        StarteddAt: selectedOrder.StarteddAt,
        FinishedAt: selectedOrder.FinishedAt,
        order: selectedOrder
      })

      // Intentar diferentes variaciones del nombre del campo de fecha de inicio
      const startDateStr = selectedOrder.StarteddAt || selectedOrder.StartedAt || selectedOrder.started_at || selectedOrder.startedAt
      
      if (startDateStr) {
        try {
          const startDate = new Date(startDateStr)
          // Validar que la fecha sea válida y no sea zero time
          if (!isNaN(startDate.getTime()) && startDate.getFullYear() >= 1900) {
            setOfStartDateTime(startDate)
            debug.log('📅 Fecha de inicio de OF sincronizada:', startDate)
          } else {
            // Fecha inválida o zero time - no establecer
            setOfStartDateTime(null)
          }
        } catch (error) {
          debug.error('❌ Error parseando fecha de inicio:', error, startDateStr)
          setOfStartDateTime(null)
        }
      } else {
        // Si no hay fecha de inicio, limpiar el estado
        debug.log('📅 No hay fecha de inicio en la orden')
        setOfStartDateTime(null)
      }

      // Intentar diferentes variaciones del nombre del campo de fecha de fin
      const endDateStr = selectedOrder.FinishedAt || selectedOrder.finished_at || selectedOrder.finishedAt
      
      if (endDateStr) {
        try {
          const endDate = new Date(endDateStr)
          // Validar que la fecha sea válida y no sea zero time
          if (!isNaN(endDate.getTime()) && endDate.getFullYear() >= 1900) {
            setOfEndDateTime(endDate)
            debug.log('📅 Fecha de fin de OF sincronizada:', endDate)
          } else {
            // Fecha inválida o zero time - no establecer
            setOfEndDateTime(null)
          }
        } catch (error) {
          debug.error('❌ Error parseando fecha de fin:', error, endDateStr)
          setOfEndDateTime(null)
        }
      } else {
        // Si no hay fecha de fin, limpiar el estado
        debug.log('📅 No hay fecha de fin en la orden')
        setOfEndDateTime(null)
      }
    } else {
      // Si no hay orden seleccionada, limpiar las fechas
      debug.log('📅 No hay orden seleccionada, limpiando fechas')
      setOfStartDateTime(null)
      setOfEndDateTime(null)
    }
  }, [selectedOrder])


  // Helper para normalizar la fábrica de una línea
  // Prioriza factory directamente, luego hierarchical_level[0] que contiene la fábrica (ej: 'CXC')
  // NO usa location directamente porque puede ser 'CXC_L01' (ubicación completa)
  const getFactoryFromLine = (line) => {
    // Priorizar factory si está disponible (ya fue asignada desde ParentID)
    if (line?.factory) {
      return line.factory
    }
    // Fallback a hierarchical_level[0]
    if (line?.hierarchical_level?.[0]) {
      return line.hierarchical_level[0]
    }
    // Si no hay nada, retornar string vacío
    return ''
  }

  // Derivar líneas de producción de los assets cargados por TanStack Query
  const lines = useMemo(() => {
    const assets = rawAssets
    if (!assets || assets.length === 0) return []

    debug.log('🔄 Derivando líneas de', assets.length, 'assets')
    debug.log('📍 Factory seleccionada:', selectedFactory, 'isGlobalView:', isGlobalView)

    // --- reutiliza la misma lógica que antes, pero sin llamadas a API ---
    const _deriveLines = () => {
      // Encontrar el ID de la fábrica seleccionada si existe
      
      let selectedFactoryId = null
      if (selectedFactory) {
        const factoryAsset = (assets || []).find(a => 
          (a.Code === selectedFactory || a.code === selectedFactory) && 
          (a.ParentID === null || a.parentID === null)
        )
        if (factoryAsset) {
          selectedFactoryId = factoryAsset.ID || factoryAsset.id
          debug.log('🏭 Fábrica seleccionada encontrada:', {
            code: factoryAsset.Code || factoryAsset.code,
            id: selectedFactoryId
          })
        } else {
          debug.warn('⚠️ No se encontró la fábrica seleccionada:', selectedFactory)
        }
      }
      
      // Filtrar solo líneas de producción
      // Estructura: Code empieza con 'Line_' o 'L', y tiene ParentID que apunta a una fábrica
      // O tiene hierarchical_level con 2 elementos (fábrica + línea)
      let linesData = (assets || []).filter(asset => {
        const code = asset.Code || asset.code || ''
        const parentId = asset.ParentID || asset.parentID
        const hierarchicalLevel = asset.hierarchical_level || asset.HierarchicalLevel
        
        // Verificar si es una línea por código
        const isLineByCode = code && (
          code.startsWith('Line_') || 
          code.startsWith('L') ||
          code.toLowerCase().includes('line') ||
          code.match(/^Line_\d+/i) || // Line_01, Line_02, etc.
          code.match(/^L\d+/i) // L01, L02, etc.
        )
        
        // Verificar si es una línea por hierarchical_level (nivel 2 = fábrica + línea)
        const isLineByLevel = hierarchicalLevel && Array.isArray(hierarchicalLevel) && hierarchicalLevel.length === 2
        
        // Debe tener un ParentID (no es null, es decir, pertenece a una fábrica)
        const hasParent = parentId !== null && parentId !== undefined
        
        const isLine = isLineByCode || isLineByLevel
        
        return isLine && hasParent
      })
      
      debug.log('🔍 Líneas encontradas después del filtro inicial:', linesData.length)
      
      // Filtrar por fábrica si se especifica
      if (selectedFactoryId !== null) {
        debug.log('🔍 Filtrando líneas por fábrica ID:', selectedFactoryId)
        const beforeFilter = linesData.length
        linesData = linesData.filter(line => {
          const lineParentId = line.ParentID || line.parentID
          const matchesFactory = lineParentId === selectedFactoryId
          
          if (!matchesFactory && isDev) {
            debug.log('❌ Línea no coincide con fábrica:', {
              lineCode: line.Code || line.code,
              lineParentId: lineParentId,
              selectedFactoryId: selectedFactoryId
            })
          }
          
          return matchesFactory
        })
        debug.log(`📊 Filtrado: ${beforeFilter} líneas antes, ${linesData.length} después del filtro por fábrica ${selectedFactory}`)
      }
      
      debug.log('📦 Assets totales:', assets?.length || 0)
      debug.log('📦 Líneas filtradas (nivel 1):', linesData.length)
      
      // Si no hay líneas con el filtro estricto, intentar un filtro más permisivo
      if (linesData.length === 0 && assets && assets.length > 0) {
        debug.warn('⚠️ No se encontraron líneas con el filtro estricto, intentando filtro más permisivo...')
        
        // Filtro más permisivo: cualquier asset con ParentID y hierarchical_level de 2 elementos
        const fallbackLines = (assets || []).filter(asset => {
          const parentId = asset.ParentID || asset.parentID
          const hierarchicalLevel = asset.hierarchical_level || asset.HierarchicalLevel
          
          // Si tiene ParentID y no es la fábrica misma (que tiene ParentID null)
          const hasParent = parentId !== null && parentId !== undefined
          
          // Si tiene hierarchical_level con 2 elementos, probablemente es una línea
          const hasTwoLevels = hierarchicalLevel && Array.isArray(hierarchicalLevel) && hierarchicalLevel.length === 2
          
          return hasParent && hasTwoLevels
        })
        
        if (fallbackLines.length > 0) {
          debug.warn(`✅ Encontradas ${fallbackLines.length} líneas con filtro permisivo`)
          linesData = fallbackLines
        } else {
          debug.warn('⚠️ No se encontraron líneas ni con el filtro permisivo')
          // Mostrar algunos assets con ParentID para entender la estructura
          const assetsWithParent = assets.filter(a => (a.ParentID || a.parentID) !== null && (a.ParentID || a.parentID) !== undefined)
          if (assetsWithParent.length > 0) {
            debug.warn('📋 Assets con ParentID (primeros 10):', assetsWithParent.slice(0, 10).map(a => ({
              code: a.Code || a.code,
              name: a.Name || a.name,
              parentId: a.ParentID || a.parentID,
              hierarchical_level: a.hierarchical_level || a.HierarchicalLevel,
              allKeys: Object.keys(a).slice(0, 10)
            })))
          }
        }
      }
      
      debug.log('✅ Líneas cargadas:', linesData.length, 'líneas')
      
      if (linesData.length === 0 && assets && assets.length > 0) {
        debug.warn('⚠️ No se encontraron líneas. Analizando estructura de assets:')
        
        // Analizar diferentes estructuras de hierarchical_level
        const level1Assets = assets.filter(a => a.hierarchical_level && a.hierarchical_level.length === 1)
        const level2Assets = assets.filter(a => a.hierarchical_level && a.hierarchical_level.length === 2)
        const level3Assets = assets.filter(a => a.hierarchical_level && a.hierarchical_level.length === 3)
        const otherAssets = assets.filter(a => !a.hierarchical_level || (a.hierarchical_level.length !== 1 && a.hierarchical_level.length !== 2 && a.hierarchical_level.length !== 3))
        
        debug.warn('📊 Análisis de assets:')
        debug.warn(`  - Assets con 1 nivel: ${level1Assets.length}`)
        debug.warn(`  - Assets con 2 niveles: ${level2Assets.length}`)
        debug.warn(`  - Assets con 3 niveles: ${level3Assets.length}`)
        debug.warn(`  - Otros assets: ${otherAssets.length}`)
        
        // Mostrar ejemplos de assets con 1 nivel
        if (level1Assets.length > 0) {
          debug.warn('📋 Ejemplos de assets con 1 nivel (primeros 10):', level1Assets.slice(0, 10).map(a => ({
            code: a.code,
            name: a.name,
            hierarchical_level: a.hierarchical_level,
            firstLevel: a.hierarchical_level?.[0],
            location: a.location,
            factory: a.factory
          })))
        }
        
        // Mostrar ejemplos completos de algunos "otros assets" para entender su estructura
        if (otherAssets.length > 0) {
          debug.warn('📋 Ejemplos de "otros assets" (primeros 5 completos):', otherAssets.slice(0, 5))
        }
        
        // Buscar assets que contengan "CXC" en alguna propiedad
        const cxcAssets = assets.filter(a => {
          const code = String(a.code || '').toUpperCase()
          const name = String(a.name || '').toUpperCase()
          const location = String(a.location || '').toUpperCase()
          const factory = String(a.factory || '').toUpperCase()
          return code.includes('CXC') || name.includes('CXC') || location.includes('CXC') || factory.includes('CXC')
        })
        if (cxcAssets.length > 0) {
          debug.warn(`📋 Assets relacionados con CXC (${cxcAssets.length}):`, cxcAssets.slice(0, 10).map(a => ({
            code: a.code,
            name: a.name,
            hierarchical_level: a.hierarchical_level,
            location: a.location,
            factory: a.factory,
            allProperties: Object.keys(a)
          })))
        }
        
        // Buscar assets que puedan ser líneas (contienen "line" o "L" en alguna propiedad)
        const potentialLines = assets.filter(a => {
          const code = String(a.code || '').toLowerCase()
          const name = String(a.name || '').toLowerCase()
          const location = String(a.location || '').toLowerCase()
          return code.includes('line') || code.match(/^l\d+/) || name.includes('line') || location.includes('line')
        })
        if (potentialLines.length > 0) {
          debug.warn(`📋 Assets que podrían ser líneas (${potentialLines.length}):`, potentialLines.slice(0, 10).map(a => ({
            code: a.code,
            name: a.name,
            hierarchical_level: a.hierarchical_level,
            location: a.location,
            factory: a.factory
          })))
        }
      } else if (linesData.length > 0) {
        debug.log('📊 Datos de líneas:', linesData.map(l => ({
          code: l.Code || l.code,
          id: l.ID || l.id,
          parentId: l.ParentID || l.parentID,
          factory: selectedFactory
        })))
      }
      
      // Mapear las líneas a un formato consistente
      // Buscar la fábrica usando el ParentID de cada línea
      const mappedLines = linesData.map(line => {
        const parentId = line.ParentID || line.parentID
        const lineCode = line.Code || line.code || ''
        
        // Buscar el asset padre (fábrica) usando el ParentID
        let factoryCode = selectedFactory || ''
        if (parentId !== null && parentId !== undefined && assets) {
          const parentAsset = assets.find(a => (a.ID || a.id) === parentId)
          if (parentAsset) {
            factoryCode = parentAsset.Code || parentAsset.code || selectedFactory || ''
            debug.log(`🏭 Fábrica encontrada para línea ${lineCode}:`, factoryCode, 'desde ParentID:', parentId)
          } else {
            debug.warn(`⚠️ No se encontró asset padre para línea ${lineCode} con ParentID:`, parentId)
          }
        } else {
          debug.warn(`⚠️ Línea ${lineCode} no tiene ParentID válido:`, parentId)
        }
        
        // Asegurar que siempre haya una fábrica asignada
        if (!factoryCode) {
          factoryCode = selectedFactory || ''
          debug.warn(`⚠️ Usando selectedFactory como fallback para línea ${lineCode}:`, factoryCode)
        }
        
        return {
          ...line,
          code: lineCode,
          id: line.ID || line.id || `line-${lineCode}`, // Asegurar que siempre haya un id único
          parentId: parentId,
          factory: factoryCode, // Siempre asignar una fábrica (nunca null/undefined)
          line: lineCode,
          // Mantener compatibilidad con código existente
          hierarchical_level: line.hierarchical_level || [factoryCode, lineCode]
        }
      })
      
      debug.log('📝 Líneas mapeadas finales:', mappedLines.length)
      return mappedLines
    }

    return _deriveLines()
  }, [rawAssets, selectedFactory])

  // En modo HMI, fijar la línea según el terminal (IP → línea en backend)
  useEffect(() => {
    if (!isHmiMode || !terminal?.bound || selectedLine) return

    const matchedLine = findLineForTerminal(lines, terminal)
    if (matchedLine) {
      setSelectedLine(matchedLine)
      return
    }

    if (!isLoadingLines && !terminalLoading) {
      const fallbackLine = buildLineFromTerminal(terminal)
      if (fallbackLine) {
        setSelectedLine(fallbackLine)
      }
    }
  }, [
    isHmiMode,
    terminal,
    lines,
    selectedLine,
    isLoadingLines,
    terminalLoading,
  ])

  // Siempre apunta a las líneas derivadas más recientes (para Actualizar tras refetch sin cerrar stale closure)
  const linesForCardsRef = useRef(lines)
  linesForCardsRef.current = lines

  // Refresca assets (/api/asset/list) y vuelve a cargar OF/recetas en las cards (POST /api/sap/orders por línea).
  const loadLines = useCallback(async () => {
    await refetchAssets()
    requestAnimationFrame(() => {
      loadLinesWithData()
    })
  }, [refetchAssets])

  // Recargar linesWithData cuando cambian los assets cargados (debe estar aquí, después de `lines`)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lines.length > 0) {
      loadLinesWithData()
    }
  // `loadLinesWithData` se define justo abajo; el lint de exhaustive-deps no lo detecta antes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines])

  const loadLinesWithData = async () => {
    const linesSnapshot = linesForCardsRef.current
    if (linesSnapshot.length === 0) {
      debug.log('⚠️ No hay líneas para cargar datos')
      setLinesWithData([])
      setLoadingLineData(false)
      return
    }
    
    try {
      setLoadingLineData(true)
      // Limitar a las primeras 10 líneas para mejorar rendimiento inicial
      const linesToLoad = linesSnapshot.slice(0, 10)
      debug.log('🔄 Cargando datos completos para', linesToLoad.length, 'líneas (de', linesSnapshot.length, 'totales)')
      
      const linesData = await Promise.all(
        linesToLoad.map(async (line) => {
          try {
            // Normalizar propiedades de línea con fallbacks robustos
            let factory = getFactoryFromLine(line)
            // Si aún no hay factory, usar selectedFactory como último recurso
            if (!factory && selectedFactory) {
              factory = selectedFactory
              debug.warn(`⚠️ Usando selectedFactory como fallback para línea ${line.code || line.line}:`, factory)
            }
            
            const prodLine = line.line || line.code || ''
            const sapCode = line.sap_code || line.code || line.line || ''
            
            if (!factory) {
              debug.error(`⚠️ No se pudo determinar la fábrica para línea:`, { 
                hierarchical_level: line.hierarchical_level, 
                factory: line.factory, 
                location: line.location,
                selectedFactory,
                line 
              })
            }
            
            debug.log(`🔄 Cargando datos para línea:`, { factory, prodLine, sapCode, location: line.location, hierarchical_level: line.hierarchical_level })
            
            if (!factory || !prodLine) {
              debug.error(`⚠️ Línea sin factory o prodLine:`, { factory, prodLine, line, selectedFactory })
              return {
                ...line,
                orders: [],
                activeOrder: null,
                recipe: null
              }
            }
            
            // Cargar órdenes para esta línea
            // Intentar sin SapRequest header primero para evitar CORS issues
            let ordersResponse
            try {
              ordersResponse = await api.post('/api/sap/orders', {}, {
                headers: {
                  'Content-Type': 'application/json',
                  'Factory': factory,
                  'ProdLine': prodLine,
                  'SapCode': sapCode,
                  'OrderFilter': 'pending',
                }
              })
            } catch (corsError) {
              // Si falla por CORS, intentar con el header (aunque probablemente también falle)
              debug.warn('⚠️ Error en primera llamada, reintentando con SapRequest header:', corsError.message)
              try {
                ordersResponse = await api.post('/api/sap/orders', {}, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Factory': factory,
                    'ProdLine': prodLine,
                    'SapCode': sapCode,
                    'SapRequest': 'false',
                    'OrderFilter': 'pending',
                  }
                })
              } catch (retryError) {
                // Si aún falla, lanzar el error original
                throw corsError
              }
            }
            
            const ordersData = Array.isArray(ordersResponse) ? ordersResponse : []
            debug.log(`✅ Órdenes cargadas para ${prodLine}:`, ordersData.length)
            
            const activeOrder = getActiveOrderFromList(ordersData)
            
            let recipeData = null
            if (activeOrder) {
              try {
                debug.log(`🔄 Cargando receta para línea ${prodLine}, orden ${activeOrder.OrderNumber}`)
                // Cargar receta para la orden activa desde SAP
                recipeData = await sapConsumptionService.getOrderRecipe(
                  factory,
                  prodLine,
                  activeOrder.OrderNumber
                )
                debug.log(`✅ Receta cargada para ${prodLine}:`, recipeData ? `${recipeData.Components?.length || 0} componentes` : 'sin datos')
              } catch (recipeErr) {
                debug.error(`❌ Error cargando receta para ${prodLine}:`, recipeErr.response?.data || recipeErr.message)
              }
            } else {
              debug.log(`⚠️ Línea ${prodLine} no tiene orden activa`)
            }
            
            return {
              ...line,
              orders: ordersData,
              activeOrder: activeOrder,
              recipe: recipeData
            }
          } catch (err) {
            debug.error(`❌ Error cargando datos para línea:`, err)
            // Retornar la línea básica sin datos completos, pero aún así mostrarla
            return {
              ...line,
              orders: [],
              activeOrder: null,
              recipe: null,
              error: err.message
            }
          }
        })
      )
      
      debug.log('✅ Datos completos cargados:', linesData.length, 'líneas')
      // Filtrar líneas válidas (que tengan al menos un id o code)
      const validLines = linesData.filter(line => line.id || line.code)
      setLinesWithData(validLines)
      
      if (validLines.length === 0 && linesSnapshot.length > 0) {
        debug.warn('⚠️ No se pudieron cargar datos para ninguna línea, pero hay', linesSnapshot.length, 'líneas disponibles')
      }
    } catch (err) {
      debug.error('Error loading lines with data:', err)
      // No mostrar error crítico, solo loguear
      // Las líneas básicas se mostrarán de todas formas
      debug.warn('⚠️ Error al cargar datos completos, pero se mostrarán las líneas básicas')
    } finally {
      setLoadingLineData(false)
    }
  }

  // ─── Thin wrappers sobre refetch de TanStack Query ──────────────────────────
  // Mantienen la firma original para compatibilidad con handlers existentes.

  const loadOrders = useCallback(async (fromSAP = false) => {
    if (!selectedLine) return
    setUpdatingSAP(fromSAP)
    try {
      const factory = dataCtx?.factory || selectedLine?.factory || selectedLine?.location || ''
      const prodLine = dataCtx?.prodLine || selectedLine?.line || selectedLine?.code || ''
      const sapCode = dataCtx?.sapCode || selectedLine?.sap_code || selectedLine?.code || selectedLine?.line || ''
      const result = fromSAP
        ? {
            data: await queryClient.fetchQuery({
              queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, true, 'pending'),
              queryFn: async () => {
                const response = await api.post('/api/sap/orders', {}, {
                  headers: {
                    'Content-Type': 'application/json',
                    Factory: factory,
                    ProdLine: prodLine,
                    SapCode: sapCode,
                    SapRequest: 'true',
                    OrderFilter: 'pending',
                  },
                })
                return Array.isArray(response) ? response : []
              },
            }),
          }
        : await refetchOrders()
      const newOrders = result.data ?? []
      if (fromSAP) {
        queryClient.setQueryData(queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending'), newOrders)
      }
      if (selectedOrder) {
        const currentOrderNumber = selectedOrder.OrderNumber || selectedOrder.orderNumber
        const updatedOrder = newOrders.find(
          o => (o.OrderNumber || o.orderNumber) === currentOrderNumber
        )
        if (updatedOrder) setSelectedOrder(updatedOrder)
      }
      if (fromSAP) showSuccess('Órdenes actualizadas desde SAP exitosamente')
    } finally {
      setUpdatingSAP(false)
    }
  }, [selectedLine, selectedOrder, refetchOrders, showSuccess, dataCtx, queryClient])

  const loadRecipe = useCallback(async (fromSAP = true) => {
    if (!selectedOrder || !selectedLine) return
    const lineCtxNorm = normalizeLineContext(selectedLine, selectedFactory)
    const orderNumberNorm = normalizeOrderNumber(selectedOrder)
    if (!lineCtxNorm || !orderNumberNorm) return

    const { factory, prodLine } = lineCtxNorm

    if (fromSAP) {
      try {
        await pullRecipeFromSapMutation.mutateAsync({
          factory,
          prodLine,
          orderNumber: orderNumberNorm,
        })
        showSuccess('Receta cargada exitosamente desde SAP')
      } catch (err) {
        debug.error('Error cargando receta desde SAP:', err)
        showError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Error al cargar la receta desde SAP'
        )
      }
    } else {
      await refetchRecipe()
    }
  }, [
    selectedOrder,
    selectedLine,
    selectedFactory,
    refetchRecipe,
    showSuccess,
    showError,
    pullRecipeFromSapMutation,
  ])

  const loadPeriods = useCallback(async () => {
    if (!selectedOrder || !selectedLine) {
      setActivePeriod(null)
      return
    }
    const result = await refetchPeriods()
    const periodsData = result.data ?? []
    const active =
      periodsData.find(p => p.IsActive === true || p.isActive === true || p.is_active === true) ??
      (periodsData.length > 0 ? periodsData[periodsData.length - 1] : null)
    setActivePeriod(active)
  }, [selectedOrder, selectedLine, refetchPeriods])

  const loadConsumptions = useCallback(async () => {
    await refetchConsumptions()
    setLastUpdate(new Date())
  }, [refetchConsumptions])

  // Función para cargar consumos de un período específico (para períodos expandidos)
  const loadPeriodConsumptions = async (periodID) => {
    if (!periodID) {
      debug.warn('⚠️ No se proporcionó periodID para cargar consumos')
      return []
    }
    
    try {
      setLoadingExpandedPeriod(periodID)
      clearError()
      
      debug.log('🔄 Cargando consumos para período expandido:', { periodID })
      
      // Nueva API: /sap/period/consumption/list (reemplaza /sap/orderConsump/list)
      const consumptionsData = await sapConsumptionService.getPeriodConsumptions(periodID)
      
      // Filtrar entradas vacías si el backend retorna un array con un elemento vacío
      const filteredConsumptions = consumptionsData.filter(c => 
        c && (c.ComponentSapCode || c.componentSapCode) && (c.ComponentSapCode || c.componentSapCode).trim() !== ''
      )
      
      debug.log('✅ Consumos cargados para período expandido:', {
        periodID: periodID,
        total: filteredConsumptions.length
      })
      
      // Guardar los consumos en el estado específico del período
      setExpandedPeriodConsumptions(prev => ({
        ...prev,
        [periodID]: filteredConsumptions
      }))
      
      return filteredConsumptions
    } catch (err) {
      debug.error('Error loading period consumptions:', err)
      // Si el endpoint no existe (404) o no está implementado, usar array vacío
      if (err.status === 404 || err.message?.includes('Not Found') || err.message?.includes('404')) {
        debug.warn('⚠️ Endpoint /sap/period/consumption/list no disponible, usando array vacío')
        setExpandedPeriodConsumptions(prev => ({
          ...prev,
          [periodID]: []
        }))
        return []
      }
      if (err.response?.status === 403) {
        showError('No tienes permisos para acceder a los consumos')
      } else {
        showError('Error al cargar los consumos del período')
      }
      setExpandedPeriodConsumptions(prev => ({
        ...prev,
        [periodID]: []
      }))
      return []
    } finally {
      setLoadingExpandedPeriod(null)
    }
  }



  // Función para calcular consumos desde InfluxDB
  // periodOverride: período específico al hacer clic en "Calcular" desde una fila expandida
  const handleCalculateConsumptions = async (periodOverride = null) => {
    if (!selectedOrder || !selectedLine) {
      showError('Selecciona una orden y línea primero')
      return
    }

    const periodToUse = periodOverride || activePeriod

    if (!periodToUse) {
      await loadPeriods()
      const periodAfterLoad = periodOverride || activePeriod
      if (!periodAfterLoad) {
        if (selectedOrder && selectedOrder.StarteddAt && !selectedOrder.FinishedAt) {
          showError('La orden está iniciada pero no tiene períodos. Haz clic en "Actualizar" en la sección de Períodos para recargar, o el período se creará automáticamente al usar "Calcular consumos".')
        } else {
          showError('No hay período activo. El primer período se crea automáticamente al iniciar una orden.')
        }
        return
      }
    }

    const period = periodToUse || periodOverride || activePeriod

    // Validar que el período no esté enviado a SAP (inmutable)
    const periodStatus = period.Status || period.status || ''
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede calcular: el período ya fue enviado a SAP y es inmutable')
      return
    }

    // Validar que el período tenga fecha de fin (evita error "empty range" de InfluxDB)
    const periodEnd = period.PeriodEnd || period.periodEnd || period.period_end
    const hasValidPeriodEnd = periodEnd && (() => {
      try {
        const d = new Date(periodEnd)
        return !isNaN(d.getTime()) && d.getTime() > 0
      } catch {
        return false
      }
    })()
    if (!hasValidPeriodEnd) {
      showError('El período debe tener fecha de fin para calcular consumos. Edita una declaración y define "Fecha y Hora Fin", o finaliza el período antes de calcular.')
      return
    }

    // Validar que haya consumos antes de calcular (solo cuando usamos activePeriod)
    if (!periodOverride && (!consumptions || consumptions.length === 0)) {
      await loadConsumptions()
      if (!consumptions || consumptions.length === 0) {
        showError('No hay consumos asignados al período. Primero debes declarar al menos un consumo (componente, dosificador y tolva) antes de calcular.')
        return
      }
    }

    try {
      setLoadingConsumptions(true)
      clearError()

      const periodID = period.ID || period.id
      debug.log('🔄 Calculando consumos para período:', {
        periodID,
        status: periodStatus,
        consumptionsCount: consumptions?.length || 0
      })
      
      // Nueva API: POST /sap/period/consumption/calculate (reemplaza GET /sap/orderConsump/Calculate)
      const response = await sapConsumptionService.calculatePeriodConsumptions(periodID)
      
      debug.log('✅ Consumos calculados para período:', {
        periodID: periodID,
        response: response,
        consumptions: response?.consumptions || response?.Consumptions || []
      })
      
      // Recargar períodos PRIMERO para asegurar que tenemos todos los consumos del período
      await loadPeriods()
      
      // Recargar consumos después del cálculo para obtener todos los consumos actualizados
      await loadConsumptions()
      
      // Si hay un período expandido, recargar sus consumos también
      if (expandedPeriodId === periodID) {
        await loadPeriodConsumptions(periodID)
      }
      
      // Recargar la receta para actualizar las cantidades declaradas
      await loadRecipe(true)
      
      showSuccess('Consumos calculados exitosamente para el período')
      setLastUpdate(new Date())
    } catch (err) {
      debug.error('Error calculating consumptions:', err)
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Error al calcular los consumos desde InfluxDB'
      showError(errorMessage)
    } finally {
      setLoadingConsumptions(false)
    }
  }

  // Función para cerrar y calcular el período
  // Función para enviar consumos a SAP (Actualizado para usar períodos)
  const handleSendToSAP = async (periodOverride = null) => {
    debug.log('🔘 [handleSendToSAP] Botón clicado - Iniciando envío a SAP')
    
    // Usar el período pasado como parámetro o el activo
    const periodToSend = periodOverride || activePeriod
    
    if (!periodToSend) {
      debug.log('❌ [handleSendToSAP] Error: No hay período para enviar')
      showError('No hay período seleccionado para enviar a SAP')
      return
    }

    const periodID = periodToSend.ID || periodToSend.id
    const periodStatus = periodToSend.Status || periodToSend.status
    
    // Validar que el período no esté ya enviado a SAP
    if (periodStatus === 'SENT_TO_SAP') {
      showError('Este período ya fue enviado a SAP y es inmutable')
      return
    }
    
    // Confirmar antes de enviar
    if (!window.confirm(`¿Estás seguro de que deseas enviar el período ${periodID} a SAP? Esta acción no se puede deshacer.`)) {
      return
    }

    try {
      debug.log('✅ [handleSendToSAP] Validaciones pasadas, iniciando envío...')
      setSendingToSAP(true)
      clearError()

      debug.log('📤 [handleSendToSAP] Enviando período a SAP:', {
        periodID: periodID,
        status: periodStatus
      })

      // Nueva API: POST /sap/period/consumption/sendToSAP (reemplaza GET /sap/orderConsump/CalcToSAP)
      const response = await sapConsumptionService.sendPeriodToSAP(periodID)
      
      debug.log('✅ [handleSendToSAP] Respuesta de SAP:', response)

      showSuccess('Período enviado a SAP exitosamente')

      // Recargar períodos para actualizar el estado (ahora será SENT_TO_SAP)
      await loadPeriods()
      // Recargar órdenes por si la OF pasó a estar totalmente enviada a SAP
      await refetchOrders()
      // Recargar consumos después del envío
      await loadConsumptions()
      // Si hay un período expandido, recargar sus consumos también
      if (expandedPeriodId === periodID) {
        await loadPeriodConsumptions(periodID)
      }
      await loadRecipe(true)
      
      setLastUpdate(new Date())
    } catch (err) {
      debug.error('Error sending to SAP:', err)
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Error al enviar consumos a SAP'
      
      // Si hay resultados parciales, mostrarlos
      if (err.response?.data?.results) {
        const results = err.response.data.results
        const successCount = results.filter(r => r.success).length
        const errorCount = results.filter(r => !r.success).length
        
        if (successCount > 0) {
          showSuccess(`✅ ${successCount} consumos enviados exitosamente`, 5000)
        }
        
        const errorDetails = results
          .filter(r => !r.success)
          .map(r => {
            const component = r.componentSapCode || 'N/A'
            const dosifier = r.dosingUnit || 'N/A'
            const error = r.errorMessage || 'Error desconocido'
            return `${component} (${dosifier}): ${error}`
          })
          .join(' | ')
        
        showError(
          `${errorMessage} - ${errorDetails}`,
          15000
        )
      } else {
        showError(errorMessage)
      }
    } finally {
      setSendingToSAP(false)
    }
  }

  // El backend actual no soporta eliminar períodos (/api/sap/period/remove).
  const handleDeletePeriod = async () => {
    showError('Eliminar períodos no está soportado por el backend actual')
  }

  const handleDeleteConsumption = async (consumption, periodOverride = null) => {
    if (!selectedOrder || !selectedLine) return
    
    // Usar período pasado como parámetro o el activo
    const periodToUse = periodOverride || activePeriod
    
    if (!periodToUse) {
      // Intentar cargar períodos primero
      await loadPeriods()
      if (!activePeriod) {
        showError('No hay período activo. El primer período se crea automáticamente al iniciar una orden.')
        return
      }
    }
    
    const period = periodToUse || activePeriod

    // Validar que el período no esté enviado a SAP (inmutable)
    const periodStatus = period.Status || period.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede eliminar: el período ya fue enviado a SAP y es inmutable')
      return
    }
    
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la declaración de ${consumption.ComponentSapCode || consumption.componentSapCode || 'este componente'}?`)) {
      return
    }
    
    try {
      setAddingConsumption(true)
      clearError()
      
      const periodID = period.ID || period.id
      const dosingUnit = consumption.DosingUnit || consumption.dosingUnit || ''
      const hopper = consumption.DosingHopper || consumption.hopper || ''
      
      // Nueva API: POST /sap/period/consumption/remove (reemplaza GET /sap/orderConsump/del)
      await sapConsumptionService.removeConsumption(periodID, dosingUnit, hopper)
      
      // Actualizar activePeriod si es necesario
      if (!periodOverride) {
        setActivePeriod(period)
      }
      
      // Recargar períodos para actualizar la lista
      await loadPeriods()
      // Recargar consumos después de eliminar (el servicio no retorna el recurso)
      await loadConsumptions()
      // Si hay un período expandido, recargar sus consumos también
      if (expandedPeriodId === (period.ID || period.id)) {
        await loadPeriodConsumptions(period.ID || period.id)
      }
      await loadRecipe(true)
      
      showSuccess('Declaración eliminada exitosamente')
    } catch (err) {
      debug.error('Error deleting consumption:', err)
      const errorMessage = err.response?.data?.message || 'Error al eliminar la declaración'
      showError(errorMessage)
    } finally {
      setAddingConsumption(false)
    }
  }

  const handleEditConsumption = async (formData, originalConsumption, periodOverride = null) => {
    if (!selectedOrder || !selectedLine) return
    
    // Usar período pasado como parámetro o el activo
    const periodToUse = periodOverride || activePeriod
    
    if (!periodToUse) {
      // Intentar cargar períodos primero
      await loadPeriods()
      if (!activePeriod) {
        showError('No hay período activo. El primer período se crea automáticamente al iniciar una orden.')
        return
      }
    }
    
    const period = periodToUse || activePeriod
    
    // Validar que el período no esté enviado a SAP (inmutable)
    const periodStatus = period.Status || period.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede editar: el período ya fue enviado a SAP y es inmutable')
      return
    }
    
    try {
      setAddingConsumption(true)
      clearError()

      const periodID = period.ID || period.id
      const currentComponent =
        (originalConsumption.ComponentSapCode || originalConsumption.componentSapCode || '').trim()
      const currentDosingUnit =
        (originalConsumption.DosingUnit || originalConsumption.dosingUnit || '').trim()
      const currentHopper =
        (originalConsumption.DosingHopper || originalConsumption.hopper || originalConsumption.Hopper || '').trim()

      const nextComponent = (formData.ComponentSapCode || '').trim()

      if (!nextComponent) {
        showError('Completa el componente para editar la declaración')
        return
      }

      const recipeComponents = Array.isArray(recipe?.Components) ? recipe.Components : []
      const allowedRecipeComponents = new Set(
        recipeComponents
          .map(c => (c?.SapCode || c?.sapCode || '').trim().toUpperCase())
          .filter(Boolean)
      )

      if (allowedRecipeComponents.size === 0) {
        showError('No hay componentes de receta disponibles para editar este consumo')
        return
      }

      if (!allowedRecipeComponents.has(nextComponent.toUpperCase())) {
        showError('El componente seleccionado no pertenece a la receta de la orden')
        return
      }

      const hasChanges = nextComponent !== currentComponent

      if (!hasChanges) {
        showSuccess('No hubo cambios para guardar')
        setShowEditModal(false)
        setEditingConsumption(null)
        return
      }

      await sapConsumptionService.updateConsumption(
        periodID,
        nextComponent,
        currentDosingUnit,
        currentHopper,
        currentDosingUnit,
        currentHopper
      )

      setShowEditModal(false)
      setEditingConsumption(null)

      await loadPeriods()
      await loadConsumptions()
      if (expandedPeriodId === periodID) {
        await loadPeriodConsumptions(periodID)
      }
      await loadRecipe(true)

      showSuccess('Declaración actualizada exitosamente')
    } catch (err) {
      debug.error('Error editing consumption:', err)
      const data = err.response?.data
      const validationErrs = Array.isArray(data?.validationErrors) ? data.validationErrors : []
      let errorMessage = data?.message || err.message || 'Error al editar el consumo'
      if (validationErrs.length > 0) {
        errorMessage = `${errorMessage}: ${validationErrs.join('; ')}`
      }
      showError(errorMessage)
      throw Object.assign(new Error(errorMessage), { response: err.response })
    } finally {
      setAddingConsumption(false)
    }
  }

  const handleEditPeriodDates = async (formData, periodOverride = null) => {
    if (!selectedOrder || !selectedLine) return

    const periodToUse = periodOverride || activePeriod
    if (!periodToUse) {
      await loadPeriods()
      if (!activePeriod) {
        showError('No hay período activo. El primer período se crea automáticamente al iniciar una orden.')
        return
      }
    }

    const period = periodToUse || activePeriod
    const periodStatus = period.Status || period.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede editar: el período ya fue enviado a SAP y es inmutable')
      return
    }

    try {
      setSavingPeriodDates(true)
      clearError()

      const minuteEpochFromDatetimeLocal = (str) => {
        if (!str || typeof str !== 'string') return null
        const d = new Date(str)
        if (isNaN(d.getTime())) return null
        return Math.floor(d.getTime() / 60000)
      }
      const minuteEpochFromServer = (raw) => {
        if (raw == null || raw === '') return null
        const d = raw instanceof Date ? raw : new Date(raw)
        if (isNaN(d.getTime())) return null
        return Math.floor(d.getTime() / 60000)
      }

      const periodUpdatedAt = period?.UpdatedAt || period?.updatedAt || period?.updated_at
      const baselineStartRaw =
        period?.PeriodStart ||
        period?.periodStart ||
        period?.period_start ||
        null
      const baselineEndRaw =
        period?.PeriodEnd ||
        period?.periodEnd ||
        period?.period_end ||
        periodUpdatedAt ||
        null

      const formStartEpoch = minuteEpochFromDatetimeLocal(formData.PeriodStart)
      const formEndEpoch = minuteEpochFromDatetimeLocal(formData.PeriodEnd)
      const baseStartEpoch = minuteEpochFromServer(baselineStartRaw)
      const baseEndEpoch = minuteEpochFromServer(baselineEndRaw)

      const periodStartChanged =
        formStartEpoch != null && baseStartEpoch != null && formStartEpoch !== baseStartEpoch
      const periodEndChanged =
        formEndEpoch != null && baseEndEpoch != null && formEndEpoch !== baseEndEpoch

      let orderDatesUpdated = false
      let orderStartUpdated = false
      let orderEndUpdated = false
      let datesUpdateNotSupported = false
      let startAdjustedViaPreviousPeriod = false
      let orderEndFromOrdersApi = false

      const factory = getFactoryFromLine(selectedLine)
      const prodLine = selectedLine.line || selectedLine.code || ''
      const orderNumber = selectedOrder?.OrderNumber || selectedOrder?.orderNumber || ''

      if (periodStartChanged || periodEndChanged) {
        if (!factory || !prodLine || !orderNumber) {
          datesUpdateNotSupported = true
        } else {
          const startDate =
            periodStartChanged && formData.PeriodStart ? new Date(formData.PeriodStart) : null
          const endDate =
            periodEndChanged && formData.PeriodEnd ? new Date(formData.PeriodEnd) : null
          if (
            (periodStartChanged && (!startDate || isNaN(startDate.getTime()))) ||
            (periodEndChanged && (!endDate || isNaN(endDate.getTime())))
          ) {
            datesUpdateNotSupported = true
          } else {
            const periodsRefresh = await refetchPeriods()
            const periodsSnapshot = Array.isArray(periodsRefresh.data)
              ? periodsRefresh.data
              : []
            let periodsForClamp = periodsSnapshot
            const pid = (p) => p?.ID ?? p?.id
            const editingPeriodId = pid(period)
            const sameId = (a, b) =>
              a != null && b != null && String(a) === String(b)

            let isFirstPeriod = true
            let isLastPeriod = true
            if (periodsSnapshot.length > 0 && editingPeriodId != null) {
              const idx = periodsSnapshot.findIndex((p) => sameId(pid(p), editingPeriodId))
              if (idx >= 0) {
                isFirstPeriod = idx === 0
                isLastPeriod = idx === periodsSnapshot.length - 1
              }
            }

            let periodEndViaPeriodApi = false

            if (periodStartChanged && !isFirstPeriod && startDate && editingPeriodId != null) {
              const currentIdx = periodsSnapshot.findIndex((p) => sameId(pid(p), editingPeriodId))
              const previousPeriod = currentIdx > 0 ? periodsSnapshot[currentIdx - 1] : null
              const previousPeriodId = pid(previousPeriod)
              if (previousPeriodId == null) {
                throw new Error('No se pudo encontrar el período anterior para ajustar el inicio')
              }
              await sapConsumptionService.updatePeriodEnd(previousPeriodId, startDate)
              startAdjustedViaPreviousPeriod = true
              const freshPeriods = await refetchPeriods()
              if (Array.isArray(freshPeriods.data)) {
                periodsForClamp = freshPeriods.data
              }
            }

            if (periodEndChanged && editingPeriodId != null) {
              // Camino A: siempre persistir el fin del período vía API de períodos.
              // Si además es el último, también actualizaremos la OF más abajo con orders/update.
              await sapConsumptionService.updatePeriodEnd(editingPeriodId, endDate)
              periodEndViaPeriodApi = true

              const freshPeriods = await refetchPeriods()
              if (Array.isArray(freshPeriods.data)) {
                periodsForClamp = freshPeriods.data
              }
            }

            let orderStartPayload = null
            let orderEndPayload = null
            if (periodStartChanged && startDate && !isNaN(startDate.getTime())) {
              if (isFirstPeriod) {
                orderStartPayload = startDate
              }
            }
            if (
              periodEndChanged &&
              endDate &&
              !isNaN(endDate.getTime()) &&
              isLastPeriod
            ) {
              orderEndPayload = endDate
              orderEndUpdated = true
            }

            const needsOrderDatesUpdate = !!(orderStartPayload || orderEndPayload)
            if (needsOrderDatesUpdate) {
              const minFinishedFromPeriods =
                computeMinimumOrderFinishedAtFromPeriods(periodsForClamp)
              const fillFinishedFromSnapshot = !(periodEndChanged && !orderEndPayload)

              await sapConsumptionService.updateOrderDates(
                factory,
                prodLine,
                orderNumber,
                orderStartPayload,
                orderEndPayload,
                {
                  orderSnapshot: selectedOrder,
                  fillFinishedAtFromSnapshot: fillFinishedFromSnapshot,
                  minimumFinishedAtFromPeriods: minFinishedFromPeriods,
                }
              )
              orderStartUpdated = !!orderStartPayload
              if (orderEndPayload) {
                orderEndUpdated = true
                orderEndFromOrdersApi = true
              }
            }

            orderDatesUpdated = periodEndViaPeriodApi || needsOrderDatesUpdate

            if (startAdjustedViaPreviousPeriod) {
              orderDatesUpdated = true
            }
          }
        }
      }

      setShowEditPeriodModal(false)
      setEditingPeriod(null)
      await loadPeriods()

      if (orderDatesUpdated) {
        await loadOrders()
        setSelectedOrder(prev => {
          if (!prev) return prev
          const next = { ...prev }
          if (orderStartUpdated && formData.PeriodStart) {
            const newStartDate = new Date(formData.PeriodStart)
            if (!isNaN(newStartDate.getTime())) {
              next.StartedAt = newStartDate
              next.StarteddAt = newStartDate
            }
          }
          if (orderEndFromOrdersApi && formData.PeriodEnd) {
            const newEndDate = new Date(formData.PeriodEnd)
            if (!isNaN(newEndDate.getTime())) {
              next.FinishedAt = newEndDate
            }
          }
          return next
        })
      }

      showSuccess(
        orderDatesUpdated
          ? startAdjustedViaPreviousPeriod
            ? 'Fechas del período actualizadas correctamente (inicio ajustado mediante el fin del período anterior)'
            : 'Fechas del período actualizadas correctamente'
          : datesUpdateNotSupported
            ? 'No se pudo actualizar fechas por contexto incompleto de línea/orden.'
            : 'No hubo cambios de fechas para actualizar'
      )
    } catch (err) {
      debug.error('Error editing period dates:', err)
      const data = err.response?.data
      const validationErrs = Array.isArray(data?.validationErrors) ? data.validationErrors : []
      let errorMessage = data?.message || err.message || 'Error al editar las fechas del período'
      if (validationErrs.length > 0) {
        errorMessage = `${errorMessage}: ${validationErrs.join('; ')}`
      }
      if (typeof errorMessage === 'string') {
        const mentionsUltimoPeriodo = /último\s+período|ultimo\s+periodo/i.test(errorMessage)
        if (
          errorMessage.includes('fecha de fin') &&
          errorMessage.includes('anterior al inicio') &&
          !mentionsUltimoPeriodo
        ) {
          errorMessage =
            'No se puede ajustar la fecha de fin a una fecha anterior a la de inicio del período'
        } else if (
          errorMessage.includes('fecha de inicio') &&
          errorMessage.includes('posterior al fin') &&
          !errorMessage.includes('primer período')
        ) {
          errorMessage =
            'No se puede ajustar la fecha de inicio a una fecha posterior a la de fin del período'
        }
      }
      showError(errorMessage)
      throw Object.assign(new Error(errorMessage), { response: err.response })
    } finally {
      setSavingPeriodDates(false)
    }
  }

  // Handler para editar solo el fin del período
  const handleEditPeriodEnd = async (formData, periodOverride = null) => {
    if (!selectedOrder || !selectedLine) return

    const periodToUse = periodOverride || editingPeriod
    if (!periodToUse) {
      showError('No se ha especificado un período para editar')
      return
    }

    const period = periodToUse
    const periodStatus = period.Status || period.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede editar: el período ya fue enviado a SAP y es inmutable')
      return
    }

    try {
      setSavingPeriodDates(true)
      clearError()

      const endDate = formData.PeriodEnd ? new Date(formData.PeriodEnd) : null
      
      if (!endDate || isNaN(endDate.getTime())) {
        showError('La fecha de fin proporcionada es inválida')
        return
      }

      const factory = getFactoryFromLine(selectedLine)
      const prodLine = selectedLine.line || selectedLine.code || ''
      const orderNumber = selectedOrder?.OrderNumber || selectedOrder?.orderNumber || ''

      if (!factory || !prodLine || !orderNumber) {
        showError('Contexto incompleto de línea/orden para actualizar fechas')
        return
      }

      const periodsRefresh = await refetchPeriods()
      const periodsSnapshot = Array.isArray(periodsRefresh.data) ? periodsRefresh.data : []
      
      const pid = (p) => p?.ID ?? p?.id
      const editingPeriodId = pid(period)
      const sameId = (a, b) => a != null && b != null && String(a) === String(b)

      let isLastPeriod = true
      if (periodsSnapshot.length > 0 && editingPeriodId != null) {
        const idx = periodsSnapshot.findIndex((p) => sameId(pid(p), editingPeriodId))
        if (idx >= 0) {
          isLastPeriod = idx === periodsSnapshot.length - 1
        }
      }

      await sapConsumptionService.updatePeriodEnd(editingPeriodId, endDate)

      // Refrescar períodos inmediatamente para capturar el ajuste automático del backend
      const freshPeriods = await refetchPeriods()
      const periodsForClamp = Array.isArray(freshPeriods.data) ? freshPeriods.data : []

      if (isLastPeriod) {
        const minFinishedFromPeriods = 
          computeMinimumOrderFinishedAtFromPeriods(periodsForClamp)

        await sapConsumptionService.updateOrderDates(
          factory,
          prodLine,
          orderNumber,
          null,
          endDate,
          {
            orderSnapshot: selectedOrder,
            fillFinishedAtFromSnapshot: false,
            minimumFinishedAtFromPeriods: minFinishedFromPeriods,
          }
        )
        
        setSelectedOrder(prev => {
          if (!prev) return prev
          return { ...prev, FinishedAt: endDate }
        })
      }

      setShowEditPeriodEndModal(false)
      setEditingPeriod(null)
      await loadPeriods()
      await loadOrders()

      showSuccess(
        isLastPeriod
          ? 'Fecha de fin del período y orden actualizadas correctamente'
          : 'Fecha de fin del período actualizada correctamente'
      )
    } catch (err) {
      debug.error('Error editing period end:', err)
      const data = err.response?.data
      const validationErrs = Array.isArray(data?.validationErrors) ? data.validationErrors : []
      let errorMessage = data?.message || err.message || 'Error al editar la fecha de fin del período'
      if (validationErrs.length > 0) {
        errorMessage = `${errorMessage}: ${validationErrs.join('; ')}`
      }
      if (typeof errorMessage === 'string') {
        const mentionsUltimoPeriodo = /último\s+período|ultimo\s+periodo/i.test(errorMessage)
        if (
          errorMessage.includes('fecha de fin') &&
          errorMessage.includes('anterior al inicio') &&
          !mentionsUltimoPeriodo
        ) {
          errorMessage =
            'No se puede ajustar la fecha de fin a una fecha anterior a la de inicio del período'
        }
      }
      showError(errorMessage)
      throw Object.assign(new Error(errorMessage), { response: err.response })
    } finally {
      setSavingPeriodDates(false)
    }
  }

  // Función para ajustar cantidad de un consumo
  const handleAdjustConsumption = async (consumption, newQuantity) => {
    if (!selectedOrder || !selectedLine) return
    
    if (!activePeriod) {
      await loadPeriods()
      if (!activePeriod) {
        showError('No hay período activo')
        return
      }
    }
    
    const periodStatus = activePeriod.Status || activePeriod.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('No se puede ajustar: el período ya fue enviado a SAP')
      return
    }
    
    try {
      setAddingConsumption(true)
      clearError()
      
      const periodID = activePeriod.ID || activePeriod.id
      const dosingUnit = consumption.DosingUnit || consumption.dosingUnit || ''
      const hopper = consumption.DosingHopper || consumption.hopper || consumption.Hopper || ''
      
      await sapConsumptionService.adjustHopperConsumption(
        periodID,
        dosingUnit,
        hopper,
        parseFloat(newQuantity)
      )
      
      setShowAdjustModal(false)
      setAdjustingConsumption(null)
      
      // Recargar períodos y consumos
      await loadPeriods()
      await loadConsumptions()
      
      showSuccess('Cantidad ajustada exitosamente')
    } catch (err) {
      debug.error('Error adjusting consumption:', err)
      const errorMessage = err.response?.data?.message || 'Error al ajustar la cantidad'
      showError(errorMessage)
    } finally {
      setAddingConsumption(false)
    }
  }

  // Crear nuevo período usando backend real: finalizar activo y crear siguiente.
  const handleCreateNewPeriod = async () => {
    if (!selectedOrder || !selectedLine) {
      showError('Selecciona una orden y línea primero')
      return
    }

    try {
      setLoadingPeriods(true)
      clearError()

      const periodStatus = activePeriod?.Status || activePeriod?.status
      const canUseFinalizeAndNext = periods.length > 0 && activePeriod && (periodStatus === 'INPROGRESS' || periodStatus === 'DRAFT')

      if (!canUseFinalizeAndNext) {
        showError('No se puede crear período manualmente con el backend actual. Inicia OF para el período 1 o usa "Finalizar y Crear Siguiente".')
        return
      }

      const periodID = activePeriod.ID || activePeriod.id
      debug.log('🔄 Creando nuevo período vía finalizeAndNext:', { periodID })

      const response = await sapConsumptionService.finalizePeriodAndNext(periodID)

      debug.log('✅ Período finalizado y siguiente creado:', response)

      showSuccess('Nuevo período creado exitosamente')

      await loadPeriods()
      await loadConsumptions()
    } catch (err) {
      debug.error('Error creando nuevo período:', err)
      const errorMessage = err.response?.data?.message || 'Error al crear nuevo período'
      showError(errorMessage)
    } finally {
      setLoadingPeriods(false)
    }
  }

  // Función para finalizar período actual y crear siguiente (Nueva funcionalidad)
  const handleFinalizePeriodAndNext = async () => {
    if (!selectedOrder || !selectedLine) {
      showError('Selecciona una orden y línea primero')
      return
    }

    if (!activePeriod) {
      showError('No hay período activo para finalizar')
      return
    }

    // Validar que el período no esté ya enviado a SAP
    const periodStatus = activePeriod.Status || activePeriod.status
    if (periodStatus === 'SENT_TO_SAP') {
      showError('Este período ya fue enviado a SAP y no se puede finalizar')
      return
    }

    if (!window.confirm('¿Estás seguro de que deseas finalizar este período y crear el siguiente?')) {
      return
    }

    try {
      setLoadingPeriods(true)
      clearError()

      const periodID = activePeriod.ID || activePeriod.id
      debug.log('🔄 Finalizando período y creando siguiente:', { periodID })

      // Nueva API: POST /sap/period/finalizeAndNext
      const response = await sapConsumptionService.finalizePeriodAndNext(periodID)

      debug.log('✅ Período finalizado y siguiente creado:', response)

      showSuccess('Período finalizado y siguiente período creado exitosamente')

      // Recargar períodos para obtener el nuevo período activo
      await loadPeriods()
      // Recargar consumos del nuevo período activo
      await loadConsumptions()
    } catch (err) {
      debug.error('Error finalizing period:', err)
      const errorMessage = err.response?.data?.message || 'Error al finalizar período y crear siguiente'
      showError(errorMessage)
    } finally {
      setLoadingPeriods(false)
    }
  }

  // Derivar dosificadores y hoppers desde rawDosingByLine (cargado por TanStack Query)
  const { dosifiersFromAPI, hoppersFromAPI } = useMemo(() => {
    const processedResponse = rawDosingByLine
    if (!processedResponse || processedResponse.length === 0) {
      return { dosifiersFromAPI: [], hoppersFromAPI: [] }
    }

    debug.log('📦 Procesando', processedResponse.length, 'dosificadores desde rawDosingByLine')

      let response
    const dosersData = []
    const hoppersData = []

    processedResponse.forEach((doser, index) => {
      const doserId = String(doser.ID || doser.Id || doser.id || doser.DoserID || doser.DoserId || `doser_${index}`)
      const doserName = doser.Name || doser.name || doser.DoserName || doser.Doser || `Dosificador ${index + 1}`
      const doserDescription = doser.Description || doser.description || doserName
      const doserLocation = doser.Location || doser.location || ''
      const doserCode = doser.Code || doser.code || doser.SapCode || doser.sap_code || doserName
      const components = doser.Components || doser.components || doser.Component || doser.component || []

      dosersData.push({ id: doserId, name: doserName, description: doserDescription, location: doserLocation, code: doserCode, rawData: doser })

      if (Array.isArray(components) && components.length > 0) {
        components.forEach((component, componentIndex) => {
          const componentId = String(
            component.ID || component.Id || component.id ||
            component.ComponentID || component.ComponentId ||
            component.PartID || component.PartId ||
            `${doserId}_component_${componentIndex}`
          )
          const componentName = component.Name || component.name || component.ComponentName || component.Component || component.PartName || component.Part || `Component ${componentIndex + 1}`
          const componentLocation = component.Location || component.location || ''
          const componentCode = component.Code || component.code || component.SapCode || component.sap_code || componentName

          hoppersData.push({ id: componentId, name: componentName, dosifierId: doserId, dosifierName: doserName, location: componentLocation, code: componentCode, rawData: component })
        })
      }
    })

    debug.log('📦 Resumen dosificadores derivados:', { dosificadores: dosersData.length, hoppers: hoppersData.length })
    return { dosifiersFromAPI: dosersData, hoppersFromAPI: hoppersData }
  }, [rawDosingByLine])

  // Función para declarar consumo
  const handleDeclareConsumption = async () => {
    if (!selectedOrder || !selectedLine) {
      showError('Selecciona una orden y línea primero')
      return
    }

    if (!activePeriod) {
      // Intentar cargar períodos primero
      await loadPeriods()
      if (!activePeriod) {
        showError('No hay período activo. El primer período se crea automáticamente al iniciar una orden. Si la orden ya está iniciada, intenta recargar los períodos.')
        return
      }
    }

    // Nota: El backend en getActivePeriod ahora crea automáticamente un nuevo período
    // si el período activo está bloqueado (SENT_TO_SAP). Por lo tanto, no necesitamos
    // validar aquí - simplemente intentamos la declaración y el backend manejará la creación.
    // Si el período está bloqueado, el backend creará uno nuevo automáticamente.
    
    const periodStatus = activePeriod?.Status || activePeriod?.status
    if (periodStatus === 'SENT_TO_SAP') {
      debug.log('⚠️ Período activo está bloqueado (SENT_TO_SAP). El backend creará automáticamente un nuevo período al procesar la declaración.')
    }

    // Validar campos requeridos
    if (!declarationForm.componentSapCode.trim() || !declarationForm.dosifierId.trim() || !declarationForm.hopperId.trim()) {
      showError('Por favor, completa todos los campos requeridos')
      return
    }

    try {
      setAddingConsumption(true)
      clearError()

      // Obtener el nombre del dosificador y hopper desde los datos cargados
      const selectedDosifier = dosifiersFromAPI.find(d => String(d.id) === String(declarationForm.dosifierId))
      const selectedHopper = hoppersFromAPI.find(h => String(h.id) === String(declarationForm.hopperId))

      if (!selectedDosifier || !selectedHopper) {
        debug.error('❌ No se encontraron dosificador o hopper:', {
          dosifierId: declarationForm.dosifierId,
          hopperId: declarationForm.hopperId,
          dosifiersDisponibles: dosifiersFromAPI.map(d => ({ id: d.id, name: d.name })),
          hoppersDisponibles: hoppersFromAPI.map(h => ({ id: h.id, name: h.name, dosifierId: h.dosifierId }))
        })
        showError('Error: No se encontraron los dosificadores o hoppers seleccionados')
        return
      }

      debug.log('✅ Dosificador y hopper seleccionados:', {
        dosifier: {
          id: selectedDosifier.id,
          name: selectedDosifier.name,
          rawData: selectedDosifier.rawData
        },
        hopper: {
          id: selectedHopper.id,
          name: selectedHopper.name,
          dosifierId: selectedHopper.dosifierId,
          rawData: selectedHopper.rawData
        }
      })

      // Obtener factory y prodLine: primero intentar como en handleEditConsumption, luego usar fallbacks como en loadOrders
      // handleEditConsumption usa directamente selectedLine.factory y selectedLine.line
      // Pero si no están disponibles, usar getFactoryFromLine como en loadOrders
      const factory = selectedLine.factory || getFactoryFromLine(selectedLine)
      const prodLine = selectedLine.line || selectedLine.code || ''

      debug.log('🔍 Propiedades de selectedLine (combinando métodos de handleEditConsumption y loadOrders):', {
        factoryDirecta: selectedLine.factory,
        factoryDesdeGetFactory: getFactoryFromLine(selectedLine),
        factoryFinal: factory,
        lineDirecta: selectedLine.line,
        code: selectedLine.code,
        prodLineFinal: prodLine,
        hierarchical_level: selectedLine.hierarchical_level,
        selectedLineCompleto: selectedLine
      })

      // Validar que todos los valores requeridos estén presentes
      if (!factory || !prodLine || !selectedDosifier.name || !selectedHopper.name || !selectedOrder.OrderNumber || !declarationForm.componentSapCode.trim()) {
        debug.error('❌ Faltan valores requeridos:', {
          factory,
          prodLine,
          machine: selectedDosifier.name,
          part: selectedHopper.name,
          orderNumber: selectedOrder.OrderNumber,
          componentCode: declarationForm.componentSapCode.trim(),
          selectedLineCompleto: selectedLine,
          hierarchical_level: selectedLine.hierarchical_level,
          factoryDirecta: selectedLine.factory,
          lineDirecta: selectedLine.line
        })
        showError('Error: Faltan datos requeridos para declarar el consumo. Verifica que la línea tenga factory y line definidos.')
        return
      }

      // Usar EXACTAMENTE el mismo formato que en handleEditConsumption
      // Asegurar que todos los valores estén en el formato correcto (sin espacios extra, etc.)
      // handleEditConsumption usa .trim() en Machine, Part y SapComponentCode
      const headers = {
        'Content-Type': 'application/json',
        'Factory': factory.trim ? factory.trim() : factory,
        'ProdLine': prodLine.trim ? prodLine.trim() : prodLine,
        'System': '',
        'Machine': selectedDosifier.name.trim(),
        'Part': selectedHopper.name.trim(),
        'SapOrderCode': selectedOrder.OrderNumber.trim ? selectedOrder.OrderNumber.trim() : selectedOrder.OrderNumber,
        'SapComponentCode': declarationForm.componentSapCode.trim()
      }
      
      // Agregar fechas de inicio y fin de OF si están disponibles
      if (ofStartDateTime && ofEndDateTime) {
        const startDateISO = new Date(ofStartDateTime).toISOString()
        const endDateISO = new Date(ofEndDateTime).toISOString()
        headers['StartDate'] = startDateISO
        headers['EndDate'] = endDateISO
        
        debug.log('📅 Agregando fechas de Inicio/Fin OF a la declaración:', {
          startDate: startDateISO,
          endDate: endDateISO
        })
      } else {
        debug.log('⚠️ No hay fechas de Inicio/Fin OF disponibles para la declaración')
      }
      
      // Asegurar que no haya valores undefined o null
      Object.keys(headers).forEach(key => {
        if (headers[key] === undefined || headers[key] === null) {
          debug.error(`⚠️ Header ${key} tiene valor undefined o null:`, headers[key])
          headers[key] = ''
        }
      })
      
      debug.log('📋 Headers finales para la API (comparar con handleEditConsumption):', {
        headers: headers,
        factory: factory,
        prodLine: prodLine,
        machine: selectedDosifier.name.trim(),
        part: selectedHopper.name.trim(),
        orderNumber: selectedOrder.OrderNumber,
        componentCode: declarationForm.componentSapCode.trim()
      })

      // Comparar headers con los de handleEditConsumption para debugging
      const expectedHeaders = {
        'Content-Type': 'application/json',
        'Factory': selectedLine.factory,
        'ProdLine': selectedLine.line,
        'System': '',
        'Machine': selectedDosifier.name.trim(),
        'Part': selectedHopper.name.trim(),
        'SapOrderCode': selectedOrder.OrderNumber,
        'SapComponentCode': declarationForm.componentSapCode.trim()
      }
      
      debug.log('🔄 Headers para declarar consumo (comparación con handleEditConsumption):', {
        headersActuales: headers,
        headersEsperados: expectedHeaders,
        sonIguales: JSON.stringify(headers) === JSON.stringify(expectedHeaders),
        factory: {
          actual: factory,
          esperado: selectedLine.factory,
          igual: factory === selectedLine.factory
        },
        prodLine: {
          actual: prodLine,
          esperado: selectedLine.line,
          igual: prodLine === selectedLine.line
        },
        machine: {
          actual: selectedDosifier.name.trim(),
          esperado: selectedDosifier.name.trim(),
          igual: true
        },
        part: {
          actual: selectedHopper.name.trim(),
          esperado: selectedHopper.name.trim(),
          igual: true
        },
        orderNumber: selectedOrder.OrderNumber,
        componentCode: declarationForm.componentSapCode.trim()
      })

      // Permitir múltiples declaraciones para la misma combinación (componente, dosificador, hopper)
      // La API manejará las declaraciones repetidas según su lógica
      debug.log('🔄 Declarando consumo (se permiten múltiples declaraciones):', {
        component: declarationForm.componentSapCode,
        dosifier: selectedDosifier.name,
        hopper: selectedHopper.name,
        headers: headers,
        url: `${import.meta.env.VITE_API_URL || ''}/sap/orderConsump/add`
      })
      
      const componentCode = declarationForm.componentSapCode.trim()
      
      // Llamar a la API del backend para declarar el consumo
      try {
        // Log detallado de lo que se va a enviar
        debug.log('📡 Llamando a la API /sap/orderConsump/add:', {
          endpoint: '/api/sap/orderConsump/add',
          headers: headers,
          headersString: JSON.stringify(headers),
          urlCompleta: `${import.meta.env.VITE_API_URL || ''}/api/sap/orderConsump/add`,
          comparacionConEdit: {
            factory: {
              declarar: factory,
              editar: selectedLine.factory,
              igual: factory === selectedLine.factory
            },
            prodLine: {
              declarar: prodLine,
              editar: selectedLine.line,
              igual: prodLine === selectedLine.line
            }
          }
        })
        
        // Nueva API: POST /sap/period/consumption/add (reemplaza GET /sap/orderConsump/add)
        const periodID = declarationTargetPeriodId || activePeriod?.ID || activePeriod?.id
        if (!periodID) {
          throw new Error('Selecciona un periodo en la tabla antes de declarar')
        }
        
        debug.log('📤 Añadiendo consumo al período:', {
          periodID,
          componentSapCode: declarationForm.componentSapCode.trim(),
          dosingUnit: selectedDosifier.name.trim(),
          hopper: selectedHopper.name.trim()
        })
        
        await sapConsumptionService.addConsumption(
          periodID,
          declarationForm.componentSapCode.trim(),
          selectedDosifier.name.trim(),
          selectedHopper.name.trim()
        )
        
        debug.log('✅ API respondió correctamente, recargando datos...')
        
        // Recargar períodos primero para asegurar que el período activo tenga el nuevo consumo
        await loadPeriods()
        
        // Recargar consumos después de añadir (el servicio no retorna el recurso)
        await new Promise(resolve => setTimeout(resolve, 300))
        await loadConsumptions()
        // Si el período activo está expandido en UI, refrescar también su cache local
        // para que el nuevo consumo aparezca sin cerrar/reabrir el desplegable.
        if (expandedPeriodId === periodID) {
          await loadPeriodConsumptions(periodID)
        }
        await loadRecipe(true)
        
        showSuccess('Consumo declarado exitosamente')
      } catch (backendErr) {
        // Si el error es 400 (Bad Request) o 409 (Conflict), mostrar el mensaje del backend
        if (backendErr.status === 400 || backendErr.status === 409) {
          const errorMessage = backendErr.message || backendErr.response?.data?.message || 'Error al procesar la solicitud'
          showError(errorMessage)
          debug.warn('⚠️ Error de validación:', errorMessage)
          // Recargar consumos para mostrar el estado actual
          await loadConsumptions()
          return
        }
        
        // Si el mensaje contiene "ya existe", también tratarlo como error de validación
        if (backendErr.message && backendErr.message.includes('ya existe')) {
          showError(backendErr.message)
          debug.warn('⚠️ Asignación duplicada:', backendErr.message)
          await loadConsumptions()
          return
        }
        
        debug.error('❌ Error al llamar a la API del backend:', {
          message: backendErr.message,
          status: backendErr.status,
          response: backendErr.response,
          component: componentCode,
          headersEnviados: headers,
          tieneFechas: !!(headers.StartDate && headers.EndDate)
        })
        
        // Si el error es 500 y tenemos fechas, podría ser que el backend no las acepta todavía
        if (backendErr.status === 500 && headers.StartDate && headers.EndDate) {
          debug.warn('⚠️ El backend rechazó la petición con fechas. Verificar si el endpoint /sap/orderConsump/add acepta StartDate y EndDate.')
        }
        
        // Mostrar mensaje de error del backend si está disponible, sino mensaje genérico
        const errorMessage = backendErr.response?.data?.message || backendErr.message || 'Error al comunicarse con el backend'
        showError(errorMessage)
      }

      // Limpiar formulario
      setDeclarationForm({
        componentSapCode: '',
        dosifierId: '',
        hopperId: ''
      })
    } catch (err) {
      // Solo errores críticos que ocurran antes de guardar la cantidad localmente
      // Construir objeto de logging de forma segura
      const errorLogData = {
        error: err,
        message: err.message,
        status: err.status,
        response: err.response
      }
      
      // Agregar headers solo si está definido (puede no estar si el error ocurre antes)
      try {
        if (typeof headers !== 'undefined') {
          errorLogData.headers = headers
        }
        if (typeof selectedDosifier !== 'undefined') {
          errorLogData.selectedDosifier = selectedDosifier
        }
        if (typeof selectedHopper !== 'undefined') {
          errorLogData.selectedHopper = selectedHopper
        }
      } catch (e) {
        // Ignorar errores al construir el log
      }
      
      errorLogData.selectedLine = selectedLine
      errorLogData.selectedOrder = selectedOrder
      
      debug.error('❌ Error crítico al declarar consumo:', errorLogData)
      
      // Intentar obtener más detalles del error
      let errorMessage = 'Error al declarar el consumo'
      
      // El servidor puede devolver mensajes específicos como "Registro no insertado"
      if (err.message && err.message !== `HTTP error! status: ${err.status}`) {
        errorMessage = err.message
        
        // Mensaje más específico para "Registro no insertado"
        if (err.message.includes('Registro no insertado')) {
          errorMessage = 'No se pudo insertar el registro. Posibles causas:\n' +
            '• Ya existe un consumo duplicado para esta combinación (componente, dosificador y hopper)\n' +
            '• Los datos enviados no son válidos según las reglas del backend\n' +
            '• Falta algún dato requerido que el backend necesita\n\n' +
            'Verifica los logs de la consola para ver los datos exactos que se están enviando.'
        }
      } else if (err.status === 500) {
        errorMessage = 'Error del servidor (500). Verifica los logs del backend para más detalles. Posibles causas: valores incorrectos en los headers o problema en el procesamiento del servidor.'
      }
      
      showError(errorMessage)
    } finally {
      setAddingConsumption(false)
    }
  }

  // Funciones para manejar Inicio y Fin de OF
  const handleOpenStartModal = () => {
    // Convertir la fecha actual a formato datetime-local si existe
    if (ofStartDateTime) {
      const date = new Date(ofStartDateTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      setTempStartDateTime(`${year}-${month}-${day}T${hours}:${minutes}`)
    } else {
      // Si no hay fecha, usar la fecha y hora actual
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      setTempStartDateTime(`${year}-${month}-${day}T${hours}:${minutes}`)
    }
    setShowStartModal(true)
  }

  const handleSaveStartDateTime = async () => {
    if (!tempStartDateTime) {
      showError('Por favor selecciona una fecha y hora válida')
      return
    }

    // Validar que haya una orden seleccionada
    if (!selectedOrder || !selectedLine) {
      showError('Por favor selecciona una orden primero')
      setShowStartModal(false)
      return
    }

    const factory = getFactoryFromLine(selectedLine)
    const prodLine = selectedLine.line || selectedLine.code || ''
    
    if (!factory || !prodLine) {
      showError('No se pudo determinar la fábrica o línea de producción')
      setShowStartModal(false)
      return
    }

    const newStartDate = new Date(tempStartDateTime)
    const orderNumber = selectedOrder.OrderNumber || selectedOrder.orderNumber

    // Se permiten múltiples órdenes activas por línea
    try {
      setSavingStartDateTime(true)

      // Determinar estado real de la OF usando el campo Status del MES
      const orderStatus = selectedOrder.Status || selectedOrder.status || 'DRAFT'
      const isCompleted = orderStatus === 'COMPLETED'
      
      // Verificar si hay un período activo antes de intentar iniciar la orden
      const hasActivePeriod = activePeriod && (activePeriod.Status === 'INPROGRESS' || activePeriod.Status === 'DRAFT' || activePeriod.status === 'INPROGRESS' || activePeriod.status === 'DRAFT')
      
      debug.log('🔍 Verificación de estado de orden:', {
        orderNumber,
        orderStatus,
        isCompleted,
        hasActivePeriod,
        activePeriod: activePeriod ? { id: activePeriod.ID || activePeriod.id, status: activePeriod.Status || activePeriod.status } : null
      })
      
      let startedNow = false
      // /start → si la OF NO está COMPLETED y no tiene período activo (primera vez o sin períodos)
      // /update → si la OF está COMPLETED (nunca crear período nuevo) o ya tiene período activo
      if (!isCompleted && !hasActivePeriod) {
        debug.log('🔄 Orden no completada y sin período activo, iniciando orden...', {
          factory,
          prodLine,
          orderNumber,
          startedAt: newStartDate.toISOString()
        })
        
        await sapConsumptionService.startProductionOrder(factory, prodLine, orderNumber, newStartDate)
        startedNow = true
        debug.log('✅ Orden iniciada correctamente')
      } else if (hasActivePeriod) {
        debug.log('ℹ️ Orden ya tiene período activo, usando /update', {
          periodID: activePeriod.ID || activePeriod.id,
          status: activePeriod.Status || activePeriod.status
        })
      } else if (isCompleted) {
        debug.log('ℹ️ Orden COMPLETED, usando /update para corregir fecha de inicio')
      }

      if (!startedNow) {
        // Actualizar la fecha de inicio con la API de actualización
        debug.log('🔄 Actualizando fecha de inicio de orden...', {
          factory,
          prodLine,
          orderNumber,
          startDate: newStartDate.toISOString()
        })

        await sapConsumptionService.updateOrderDates(
          factory,
          prodLine,
          orderNumber,
          newStartDate,
          null,
          { orderSnapshot: selectedOrder, fillFinishedAtFromSnapshot: false }
        )

        debug.log('✅ Fecha de inicio actualizada')
      } else {
        debug.log('ℹ️ Se omite orders/update porque la orden se acaba de iniciar')
      }
      
      // Refrescar solo el contexto activo (evita fan-out de requests por todas las líneas)
      await Promise.all([
        loadOrders(),
        loadPeriods(),
      ])
      
      // Actualizar el estado local inmediatamente
      setOfStartDateTime(newStartDate)
      debug.log('✅ Fecha de inicio actualizada localmente:', newStartDate)
      
      showSuccess('Fecha y hora de inicio de OF guardada e iniciada correctamente')
      setShowStartModal(false)
    } catch (error) {
      debug.error('❌ Error al guardar fecha de inicio:', error)
      const errMsg = error.response?.data?.message || error.message || ''
      const activeOrderMsg = formatActiveOrderConflictMessage(errMsg)
      showError(activeOrderMsg || errMsg || 'Error al guardar la fecha de inicio de OF')
    } finally {
      setSavingStartDateTime(false)
    }
  }

  const handleOpenEndModal = () => {
    // Convertir la fecha actual a formato datetime-local si existe
    if (ofEndDateTime) {
      const date = new Date(ofEndDateTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      setTempEndDateTime(`${year}-${month}-${day}T${hours}:${minutes}`)
    } else {
      // Si no hay fecha, usar la fecha y hora actual
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      setTempEndDateTime(`${year}-${month}-${day}T${hours}:${minutes}`)
    }
    setShowEndModal(true)
  }

  const handleSaveEndDateTime = async () => {
    if (!tempEndDateTime) {
      showError('Selecciona fecha y hora de fin')
      return
    }

    if (!selectedOrder || !selectedLine) {
      showError('Selecciona una línea y orden primero')
      return
    }

    const newEndDate = new Date(tempEndDateTime)
    if (isNaN(newEndDate.getTime())) {
      showError('Fecha/hora de fin inválida')
      return
    }

    const factory = getFactoryFromLine(selectedLine)
    const prodLine = selectedLine.line || selectedLine.code || ''
    const orderNumber = selectedOrder.OrderNumber || selectedOrder.orderNumber

    try {
      setSavingEndDateTime(true)

      const isOrderInProgress = activePeriod && (activePeriod.Status === 'INPROGRESS' || activePeriod.status === 'INPROGRESS')

      if (isOrderInProgress) {
        // La OF está activa: solo finalizar, el backend cierra el período y pone time.Now()
        await sapConsumptionService.finishProductionOrder(factory, prodLine, orderNumber)
      } else {
        // La OF ya está COMPLETED: actualizar solo la fecha de fin (sin enviar StartedAt)
        await sapConsumptionService.updateOrderDates(
          factory,
          prodLine,
          orderNumber,
          null,
          newEndDate,
          { orderSnapshot: selectedOrder, fillStartedAtFromSnapshot: false }
        )
      }

      // Recargar solo el contexto activo para evitar fan-out de requests
      await Promise.all([
        loadOrders(),
        loadPeriods(),
      ])

      setOfEndDateTime(newEndDate)
      showSuccess('Orden de fabricación finalizada correctamente')
      setShowEndModal(false)
    } catch (error) {
      debug.error('❌ Error al finalizar OF:', error)
      const errMsg = error.response?.data?.message || error.message || 'Error al finalizar la orden de fabricación'
      showError(errMsg)
    } finally {
      setSavingEndDateTime(false)
    }
  }

  // Función para formatear fecha y hora para mostrar
  const formatDateTime = (date) => {
    if (!date) return 'No establecida'
    const d = new Date(date)
    return d.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Formatear fecha/hora para declaraciones (formato: YYYY-MM-DD HH:mm:ss.SSS +TZ)
  const formatConsumptionDateTime = (date) => {
    // Manejar null, undefined, o string vacío (comportamiento esperado, no requiere warning)
    if (!date || date === 'null' || date === 'undefined' || date === '' || (typeof date === 'string' && date.trim() === '')) {
      return null // Retornar null para que el componente no muestre nada
    }
    
    // Si ya es un string en formato ISO (RFC3339), parsearlo
    let d
    if (typeof date === 'string') {
      d = new Date(date)
    } else {
      d = new Date(date)
    }
    
    // Verificar si la fecha es válida
    if (isNaN(d.getTime())) {
      // Fecha inválida - retornar null sin warning (caso esperado)
      return null
    }
    
    // Verificar si es una fecha zero time (0001-01-01 o similar)
    // Las fechas zero time son válidas en el contexto de la aplicación (indican "no establecida")
    // No mostrar warning ya que es un comportamiento esperado
    if (d.getFullYear() < 1900) {
      return null
    }
    
    // Obtener año, mes, día
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    
    // Obtener hora, minuto, segundo, milisegundos
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    const seconds = String(d.getSeconds()).padStart(2, '0')
    const milliseconds = String(d.getMilliseconds()).padStart(3, '0')
    
    // Obtener timezone offset
    const offset = -d.getTimezoneOffset()
    const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
    const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0')
    const offsetSign = offset >= 0 ? '+' : '-'
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds} ${offsetSign}${offsetHours}${offsetMinutes}`
  }


  // dosifiersFromAPI y hoppersFromAPI se derivan reactivamente de rawDosingByLine (ver useMemo arriba)

  // Funciones CRUD para Dosificadores
  const loadDosifiers = useCallback(() => {
    // Extraer dosificadores únicos de los consumos
    const dosersList = [...new Set(consumptions.map(c => c.DosingUnit).filter(Boolean))].sort()
    const dosersData = dosersList.map(doser => ({
      id: doser,
      name: doser
    }))
    setDosifiers(dosersData)
  }, [consumptions])

  const handleAddDosifier = async (formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      // Por ahora, solo actualizamos el estado local
      // En el futuro, esto podría llamar a un endpoint del backend
      setDosifiers(prev => [...prev, { id: formData.name, name: formData.name, description: formData.description }])
      showSuccess('Dosificador agregado exitosamente')
      loadDosifiers()
    } catch (err) {
      debug.error('Error adding dosifier:', err)
      showError('Error al agregar el dosificador')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleEditDosifier = async (id, formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      setDosifiers(prev => prev.map(d => d.id === id ? { ...d, ...formData } : d))
      showSuccess('Dosificador editado exitosamente')
    } catch (err) {
      debug.error('Error editing dosifier:', err)
      showError('Error al editar el dosificador')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleDeleteDosifier = async (id) => {
    try {
      setManagingCRUD(true)
      clearError()
      setDosifiers(prev => prev.filter(d => d.id !== id))
      showSuccess('Dosificador eliminado exitosamente')
    } catch (err) {
      debug.error('Error deleting dosifier:', err)
      showError('Error al eliminar el dosificador')
    } finally {
      setManagingCRUD(false)
    }
  }

  // Funciones CRUD para Hoppers
  const loadHoppers = useCallback(() => {
    // Extraer hoppers únicos de los consumos
    const uniqueHoppers = []
    const seen = new Set()
    
    consumptions.forEach(consumption => {
      const doser = consumption.DosingUnit || 'Sin Dosificador'
      const hopper = consumption.DosingHopper || 'Sin Hopper'
      const key = `${doser}-${hopper}`
      
      if (!seen.has(key)) {
        seen.add(key)
        uniqueHoppers.push({
          id: key,
          name: hopper,
          dosifier: doser,
          dosifierId: doser
        })
      }
    })
    
    setHoppers(uniqueHoppers)
  }, [consumptions])

  const handleAddHopper = async (formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      setHoppers(prev => [...prev, {
        id: `${formData.dosifierId}-${formData.name}`,
        name: formData.name,
        dosifier: formData.dosifierId,
        dosifierId: formData.dosifierId,
        description: formData.description
      }])
      showSuccess('Hopper agregado exitosamente')
      loadHoppers()
    } catch (err) {
      debug.error('Error adding hopper:', err)
      showError('Error al agregar el hopper')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleEditHopper = async (id, formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      setHoppers(prev => prev.map(h => h.id === id ? { ...h, ...formData } : h))
      showSuccess('Hopper editado exitosamente')
    } catch (err) {
      debug.error('Error editing hopper:', err)
      showError('Error al editar el hopper')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleDeleteHopper = async (id) => {
    try {
      setManagingCRUD(true)
      clearError()
      setHoppers(prev => prev.filter(h => h.id !== id))
      showSuccess('Hopper eliminado exitosamente')
    } catch (err) {
      debug.error('Error deleting hopper:', err)
      showError('Error al eliminar el hopper')
    } finally {
      setManagingCRUD(false)
    }
  }

  // Funciones CRUD para Componentes
  const loadComponents = useCallback(() => {
    // Extraer componentes únicos de los consumos
    const compsList = [...new Set(consumptions.map(c => c.ComponentSapCode).filter(Boolean))].sort()
    const compsData = compsList.map(comp => ({
      id: comp,
      sapCode: comp,
      ComponentSapCode: comp
    }))
    setComponents(compsData)
  }, [consumptions])

  const handleAddComponent = async (formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      setComponents(prev => [...prev, {
        id: formData.sapCode,
        sapCode: formData.sapCode,
        ComponentSapCode: formData.sapCode,
        description: formData.description,
        unit: formData.unit
      }])
      showSuccess('Componente agregado exitosamente')
      loadComponents()
    } catch (err) {
      debug.error('Error adding component:', err)
      showError('Error al agregar el componente')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleEditComponent = async (id, formData) => {
    try {
      setManagingCRUD(true)
      clearError()
      setComponents(prev => prev.map(c => c.id === id ? { ...c, ...formData, ComponentSapCode: formData.sapCode } : c))
      showSuccess('Componente editado exitosamente')
    } catch (err) {
      debug.error('Error editing component:', err)
      showError('Error al editar el componente')
    } finally {
      setManagingCRUD(false)
    }
  }

  const handleDeleteComponent = async (id) => {
    try {
      setManagingCRUD(true)
      clearError()
      setComponents(prev => prev.filter(c => c.id !== id))
      showSuccess('Componente eliminado exitosamente')
    } catch (err) {
      debug.error('Error deleting component:', err)
      showError('Error al eliminar el componente')
    } finally {
      setManagingCRUD(false)
    }
  }

  // Cargar datos cuando cambian los consumos
  useEffect(() => {
    if (consumptions.length > 0) {
      loadDosifiers()
      loadHoppers()
      loadComponents()
    }
  }, [consumptions, loadDosifiers, loadHoppers, loadComponents])


  if (loading) {
    return <LoadingState message="Cargando líneas de producción..." fullScreen />
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header refactorizado */}
        <PageHeader
          currentFactory={selectedFactory}
          isGlobalView={isGlobalView}
          lastUpdate={lastUpdate}
        />

        {/* Notificaciones refactorizadas con accesibilidad */}
        <Notifications
          error={error}
          success={success}
          onClearError={clearError}
          onClearSuccess={clearSuccess}
        />

        {/* Line Selection - Vista Cards (oculta en HMI: la línea la fija el terminal) */}
        {!selectedLine && isHmiMode && (terminalLoading || isLoadingLines) && (
          <Card className="mb-6">
            <div className="p-12 text-center">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Cargando línea del terminal…</p>
            </div>
          </Card>
        )}

        {!selectedLine && !isHmiMode && (
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">
                  Seleccionar Línea de Producción
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setUpdatingSAP(true)
                      // Sincronizar todas las líneas con SAP
                      Promise.all(lines.map(line => {
                        const sapCode = line.sap_code || line.code || line.line || ''
                        return api.post('/api/sap/orders', {}, {
                          headers: {
                            'Content-Type': 'application/json',
                            'Factory': line.factory,
                            'ProdLine': line.line,
                            'SapCode': sapCode,
                            'SapRequest': 'true'
                          },
                          
                        }).catch(err => {
                          debug.error(`Error sincronizando línea ${line.line}:`, err)
                          return null
                        })
                      })).then(() => {
                        showSuccess('Todas las líneas sincronizadas con SAP exitosamente')
                        // Recargar datos de las líneas
                        loadLinesWithData()
                      }).catch(err => {
                        debug.error('Error en sincronización masiva:', err)
                        showError('Error al sincronizar algunas líneas con SAP')
                      }).finally(() => {
                        setUpdatingSAP(false)
                      })
                    }}
                    disabled={updatingSAP}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
                  >
                    {updatingSAP ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span>Sincronizar SAP (Todas)</span>
                  </button>
                  <button
                    onClick={loadLines}
                    disabled={loading || loadingLineData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
                  >
                    {loading || loadingLineData ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span>Actualizar</span>
                  </button>
                </div>
              </div>
              {loadingLineData ? (
                <LoadingState message="Cargando información completa de las líneas..." />
              ) : (linesWithData.length === 0 && lines.length === 0) ? (
                <EmptyState
                  icon={Factory}
                  title="No hay líneas disponibles"
                  description={`No se encontraron líneas de producción${selectedFactory ? ` para la fábrica ${selectedFactory}` : ''}`}
                />
              ) : (
                <div className="space-y-4">
                  {/* Mostrar líneas con datos completos primero */}
                  {linesWithData.length > 0 && linesWithData.map((line) => {
                    const activeOrder = line.activeOrder
                    const recipe = line.recipe
                    const progress = activeOrder && activeOrder.QuantityToProduce > 0
                      ? Math.round((activeOrder.QuantityProduced / activeOrder.QuantityToProduce) * 100)
                      : 0
                    
                    return (
                      <Card 
                        key={line.id}
                        className="cursor-pointer hover:border-blue-500 transition-all"
                        onClick={() => {
                          setSelectedLine(line)
                          setSelectedOrder(null)
                          setActivePeriod(null)
                        }}
                      >
                        <div className="p-6">
                          {/* Header de la línea */}
                          <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-4">
                            <div className="flex items-center gap-4">
                              <Factory className="w-8 h-8 text-blue-400" />
                              <div>
                                <h3 className="text-2xl font-bold text-white">{line.line}</h3>
                                <p className="text-sm text-gray-400">{line.location || line.factory} • <span className="font-mono">{line.code || line.sap_code || 'N/A'}</span></p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={line.influx_available ? 'success' : 'secondary'}>
                                {line.influx_available ? 'Activa' : 'Sin datos'}
                              </Badge>
                              <Badge 
                                variant={
                                  line.status?.toLowerCase() === 'running' || line.status?.toLowerCase() === 'operando' ? 'success' :
                                  line.status?.toLowerCase() === 'stopped' || line.status?.toLowerCase() === 'detenida' ? 'danger' :
                                  line.status?.toLowerCase() === 'maintenance' || line.status?.toLowerCase() === 'mantenimiento' ? 'warning' :
                                  'secondary'
                                }
                              >
                                {line.status === 'running' ? 'Operando' :
                                 line.status === 'stopped' ? 'Detenida' :
                                 line.status === 'maintenance' ? 'Mantenimiento' :
                                 line.status || 'Desconocido'}
                              </Badge>
                              {line.oee !== undefined && line.oee !== null && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">OEE</p>
                                  <p className={`text-lg font-bold ${
                                    line.oee >= 85 ? 'text-green-500' :
                                    line.oee >= 70 ? 'text-yellow-500' :
                                    'text-red-500'
                                  }`}>
                                    {line.oee.toFixed(1)}%
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Grid de 3 columnas: Línea | OF | Receta */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Columna 1: Métricas de Línea */}
                            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                              <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Métricas de Línea</h4>
                              {(line.speed !== undefined && line.speed !== null) || 
                               (line.production !== undefined && line.production !== null) || 
                               (line.oee !== undefined && line.oee !== null) ||
                               line.last_update ? (
                                <div className="space-y-3">
                                  {line.speed !== undefined && line.speed !== null && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-gray-400">Velocidad:</span>
                                      <span className="text-lg font-bold text-white font-mono">{line.speed.toFixed(1)} <span className="text-sm text-gray-500">kg/h</span></span>
                                    </div>
                                  )}
                                  {line.production !== undefined && line.production !== null && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-gray-400">Producción:</span>
                                      <span className="text-lg font-bold text-white font-mono">{line.production.toFixed(0)} <span className="text-sm text-gray-500">m</span></span>
                                    </div>
                                  )}
                                  {line.oee !== undefined && line.oee !== null && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-gray-400">OEE:</span>
                                      <span className={`text-lg font-bold font-mono ${
                                        line.oee >= 85 ? 'text-green-500' :
                                        line.oee >= 70 ? 'text-yellow-500' :
                                        'text-red-500'
                                      }`}>
                                        {line.oee.toFixed(1)}%
                                      </span>
                                    </div>
                                  )}
                                  {line.last_update && (
                                    <div className="pt-2 border-t border-gray-700">
                                      <p className="text-xs text-gray-500">Última actualización</p>
                                      <p className="text-sm text-gray-300">
                                        {new Date(line.last_update).toLocaleString('es-ES', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full min-h-[150px]">
                                  <div className="text-center">
                                    <Package className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-400">Sin métricas disponibles</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Columna 2: Orden de Fabricación */}
                            <div
                              className={cn(
                                'rounded-lg p-4 border',
                                activeOrder
                                  ? 'border-green-600/60 bg-green-900/10'
                                  : 'border-amber-600/40 border-dashed bg-amber-900/5'
                              )}
                            >
                              <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Orden Activa</h4>
                              {activeOrder ? (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <span className="relative flex h-2 w-2">
                                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                                    </span>
                                    <p className="text-lg font-bold text-green-300 font-mono">{activeOrder.OrderNumber}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Producto</p>
                                    <p className="text-sm text-white font-medium line-clamp-2">{activeOrder.ProductName || '-'}</p>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-700">
                                    <div className="text-center">
                                      <p className="text-xs text-gray-500">A Producir</p>
                                      <p className="text-lg font-bold text-blue-400">{activeOrder.QuantityToProduce || '0'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-xs text-gray-500">Producido</p>
                                      <p className="text-lg font-bold text-green-400">{activeOrder.QuantityProduced || '0'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-xs text-gray-500">Pendiente</p>
                                      <p className="text-lg font-bold text-orange-400">{activeOrder.QuantityRemainedToProduce || '0'}</p>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <p className="text-xs text-gray-400">Progreso</p>
                                      <p className="text-sm font-bold text-white">{progress}%</p>
                                    </div>
                                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                                      <div
                                        className="h-full bg-gradient-to-r from-blue-600 to-green-500 transition-all duration-500"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full min-h-[150px]">
                                  <div className="text-center">
                                    <Package className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                                    <p className="text-sm font-medium text-amber-400">Sin orden activa</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Entra en la línea e inicia una OF con Inicio OF
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Columna 3: Receta y Componentes */}
                            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                              <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Receta y Componentes</h4>
                              {recipe && recipe.Components && recipe.Components.length > 0 ? (
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-xs text-gray-500">Código SAP Receta</p>
                                    <p className="text-sm font-mono text-white">{recipe.SapCode || activeOrder?.OrderNumber || '-'}</p>
                                  </div>
                                  <div className="pt-2 border-t border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                      <p className="text-xs text-gray-500">Componentes</p>
                                      <Badge variant="info" className="text-xs">
                                        {recipe.Components.length}
                                      </Badge>
                                    </div>
                                    <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
                                      {recipe.Components.slice(0, 5).map((component, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-gray-900/50 rounded p-2">
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-mono text-gray-400 truncate">{component.SapCode || '-'}</p>
                                            <p className="text-xs text-gray-500 truncate">{component.Description || '-'}</p>
                                          </div>
                                          <div className="text-right ml-2">
                                            <p className="text-xs font-medium text-white">{component.RequiredQuantity || '0'}</p>
                                            <p className="text-xs text-gray-500">{component.MeasurementUnitRQ || ''}</p>
                                          </div>
                                        </div>
                                      ))}
                                      {recipe.Components.length > 5 && (
                                        <p className="text-xs text-gray-500 text-center pt-1">
                                          +{recipe.Components.length - 5} más...
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full min-h-[150px]">
                                  <div className="text-center">
                                    <Package className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-400">Sin receta cargada</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    )
                  })}
                  
                  {/* Mostrar líneas sin datos completos (si hay líneas que no están en linesWithData o si linesWithData está vacío) */}
                  {(lines.length > linesWithData.length || linesWithData.length === 0) && lines
                    .filter(line => linesWithData.length === 0 || !linesWithData.find(lwd => (lwd.id || lwd.code) === (line.id || line.code)))
                    .map((line) => {
                      return (
                        <Card 
                          key={line.id || line.code}
                          className="cursor-pointer hover:border-blue-500 transition-all"
                          onClick={() => {
                            setSelectedLine(line)
                            setSelectedOrder(null)
                            setActivePeriod(null)
                          }}
                        >
                          <div className="p-6">
                            {/* Header de la línea */}
                            <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-4">
                              <div className="flex items-center gap-4">
                                <Factory className="w-8 h-8 text-blue-400" />
                                <div>
                                  <h3 className="text-2xl font-bold text-white">{line.line || line.code}</h3>
                                  <p className="text-sm text-gray-400">{line.location || line.factory} • <span className="font-mono">{line.code || line.sap_code || 'N/A'}</span></p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="secondary">
                                  Sin datos
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Información básica de la línea */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                                <span className="text-sm text-gray-400">Estado:</span>
                                <Badge variant="secondary">Disponible</Badge>
                              </div>
                              <div className="text-center py-4">
                                <p className="text-sm text-gray-400 mb-2">Haz clic para ver detalles y órdenes</p>
                                {loadingLineData && (
                                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Cargando información adicional...</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      )
                    })}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Order Selection */}
        {selectedLine && !selectedOrder && (
          <>
            <Card className="mb-6">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {!isHmiMode && (
                    <button
                      onClick={() => {
                        setSelectedLine(null)
                        setSelectedOrder(null)
                        setActivePeriod(null)
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
                    </button>
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedLine.line}</h2>
                      <p className="text-gray-400">{selectedLine.location || selectedLine.factory}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {loadingOrders ? (
              <Card>
                <div className="p-12 text-center">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Cargando órdenes de producción...</p>
                </div>
              </Card>
            ) : (
              <>
                <Card className="mb-6">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold text-white">
                        Órdenes de Fabricación ({orders.length})
                      </h2>
                      <button
                        onClick={() => loadOrders(true)}
                        disabled={updatingSAP}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
                      >
                        {updatingSAP ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        <span>SAP UPDATE</span>
                      </button>
                    </div>

                    {lineActiveOrder ? (
                      <div className="flex items-center gap-3 rounded-lg border border-green-600/50 bg-green-900/20 px-4 py-3">
                        <span className="relative flex h-3 w-3 shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-green-300">Orden activa de la línea</p>
                          <p className="text-xs text-green-200/80 font-mono truncate">{lineActiveOrder.OrderNumber}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrder(lineActiveOrder)
                            setActivePeriod(null)
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
                          }}
                          className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 hover:bg-green-500 text-white transition-colors"
                        >
                          Ir a la orden activa
                        </button>
                      </div>
                    ) : orders.length > 0 ? (
                      <div className="flex items-start gap-3 rounded-lg border border-amber-600/40 border-dashed bg-amber-900/10 px-4 py-3">
                        <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-300">Sin orden activa en esta línea</p>
                          <p className="text-xs text-amber-200/70 mt-0.5">
                            Selecciona una orden pendiente y pulsa <strong>Inicio OF</strong> para iniciarla.
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Card>

                {orders.length === 0 ? (
                  <Card>
                    <div className="p-12 text-center">
                      <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 text-lg">No hay órdenes de producción disponibles</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Pulsa SAP UPDATE para sincronizar desde SAP o selecciona una orden cuando aparezca en la lista.
                      </p>
                    </div>
                  </Card>
                ) : (
                <div className="space-y-4">{sortedOrders.map((order) => {
                  const progress = order.QuantityToProduce > 0
                    ? Math.round((order.QuantityProduced / order.QuantityToProduce) * 100)
                    : 0
                  const isLineActiveOrder = isOrderActiveInMES(order)
                  const { label: statusLabel, badgeClass: statusBadgeClass } = getOrderDisplayStatus(order)
                  const isSelected = selectedOrder && selectedOrder.OrderNumber === order.OrderNumber
                  
                  return (
                    <Card 
                      key={order.OrderNumber}
                      className={cn(
                        'transition-all cursor-pointer',
                        isLineActiveOrder
                          ? 'border-2 border-green-500 bg-green-900/10 ring-1 ring-green-500/30'
                          : isSelected
                            ? 'border-blue-500 border-2'
                            : 'hover:border-blue-500'
                      )}
                      onClick={() => {
                        setSelectedOrder(order)
                        setActivePeriod(null)
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
                      }}
                    >
                      <div className="p-6">
                        {/* Header de la orden */}
                        <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-4">
                          <div className="flex items-center gap-4">
                            <Package className={cn('w-8 h-8', isLineActiveOrder ? 'text-green-400' : 'text-blue-400')} />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-2xl font-bold text-white font-mono">{order.OrderNumber}</h3>
                                {isLineActiveOrder && (
                                  <Badge className="bg-green-600 text-white text-xs">
                                    ● Orden activa de la línea
                                  </Badge>
                                )}
                                {isSelected && (
                                  <Badge variant="info" className="text-xs">
                                    Seleccionada
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-400">{order.ProductName || '-'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={statusBadgeClass}>
                              {statusLabel}
                            </Badge>
                            {order.MeasurementUnit && (
                              <Badge variant="info">{order.MeasurementUnit}</Badge>
                            )}
                          </div>
                        </div>

                        {/* Grid de información */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Columna 1: Información del Producto */}
                          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                            <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Información del Producto</h4>
                            <div className="space-y-3">
                              <div>
                                <p className="text-xs text-gray-500">Código Producto</p>
                                <p className="text-lg font-bold text-white font-mono">{order.ProductName || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Descripción</p>
                                <p className="text-sm text-gray-300 line-clamp-2" title={order.ProductDescription}>
                                  {order.ProductDescription || '-'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Columna 2: Cantidades y Progreso */}
                          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                            <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Progreso de Producción</h4>
                            <div className="space-y-4">
                              <div className="grid grid-cols-3 gap-3">
                                <div className="text-center">
                                  <p className="text-xs text-gray-500 mb-1">A Producir</p>
                                  <p className="text-2xl font-bold text-blue-400">{order.QuantityToProduce || '0'}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-gray-500 mb-1">Producido</p>
                                  <p className="text-2xl font-bold text-green-400">{order.QuantityProduced || '0'}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-gray-500 mb-1">Pendiente</p>
                                  <p className="text-2xl font-bold text-orange-400">{order.QuantityRemainedToProduce || '0'}</p>
                                </div>
                              </div>
                              
                              {/* Barra de Progreso */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <p className="text-xs text-gray-400">Progreso</p>
                                  <p className="text-sm font-bold text-white">{progress}%</p>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-blue-600 to-green-500 transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}</div>
                )}
              </>
            )}
          </>
        )}

        {/* === REORGANIZED ORDER DETAIL VIEW === */}
        {selectedLine && selectedOrder && (
          <>
            {/* 1. Compact Header: order info + OF Start/End buttons */}
            <OrderDetailHeader
              selectedOrder={selectedOrder}
              selectedLine={selectedLine}
              ofStartDateTime={ofStartDateTime}
              ofEndDateTime={ofEndDateTime}
              formatDateTime={formatDateTime}
              loadingConsumptions={loadingConsumptions}
              loadingRecipe={loadingRecipe}
              onBack={() => {
                setSelectedOrder(null)
                setActivePeriod(null)
              }}
              onRefresh={() => {
                loadConsumptions()
                loadRecipe()
              }}
              onOpenStartModal={handleOpenStartModal}
              onOpenEndModal={handleOpenEndModal}
            />


            {/* 2. Two-column layout: OF Summary + Recipe */}
            <OrderSummaryAndRecipe
              selectedOrder={selectedOrder}
              recipe={recipe}
              consumptions={consumptions}
              declaredQuantitiesByComponent={declaredQuantitiesByComponent}
              loadingRecipe={loadingRecipe}
              loadingRecipeFromSap={pullRecipeFromSapMutation.isPending}
              onLoadRecipeFromSAP={loadRecipe}
            />

            {/* 3. Unified Consumption Periods Manager */}
            <ConsumptionPeriodsManager
              periods={periods}
              activePeriod={activePeriod}
              loadingPeriods={loadingPeriods}
              expandedPeriodId={expandedPeriodId}
              expandedPeriodConsumptions={expandedPeriodConsumptions}
              loadingExpandedPeriod={loadingExpandedPeriod}
              loadingConsumptions={loadingConsumptions}
              sendingToSAP={sendingToSAP}
              selectedOrder={selectedOrder}
              selectedLine={selectedLine}
              recipe={recipe}
              declarationForm={declarationForm}
              setDeclarationForm={setDeclarationForm}
              dosifiersFromAPI={dosifiersFromAPI}
              hoppersFromAPI={hoppersFromAPI}
              loadingDosifiersAPI={loadingDosifiersAPI}
              addingConsumption={addingConsumption}
              onCreateNewPeriod={handleCreateNewPeriod}
              onLoadPeriods={loadPeriods}
              onSetActivePeriod={async () => {
                showError('Marcar período activo no está soportado por el backend actual')
              }}
              onExpandPeriod={(periodID) => setExpandedPeriodId(periodID)}
              onCollapsePeriod={(periodID) => {
                setExpandedPeriodId(null)
                setExpandedPeriodConsumptions(prev => {
                  const newState = { ...prev }
                  delete newState[periodID]
                  return newState
                })
              }}
              onLoadPeriodConsumptions={loadPeriodConsumptions}
              onCalculateConsumptions={async (period, periodID) => {
                setActivePeriod(period)
                await handleCalculateConsumptions(period)
                await loadPeriodConsumptions(periodID)
              }}
              onSendToSAP={async (period) => {
                await handleSendToSAP(period)
              }}
              onDeletePeriod={handleDeletePeriod}
              onFinalizePeriodAndNext={handleFinalizePeriodAndNext}
              onDeclareConsumption={handleDeclareConsumption}
              onReloadDosifiers={refetchDosingByLine}
              selectedPeriodId={declarationTargetPeriodId}
              onSelectedPeriodChange={setDeclarationTargetPeriodId}
              onEditPeriodEnd={(period) => {
                setActivePeriod(period)
                setEditingPeriod(period)
                setShowEditPeriodEndModal(true)
              }}
              onEditConsumption={(consumption, period) => {
                setActivePeriod(period)
                const consumptionWithPeriod = { ...consumption, _period: period }
                setEditingConsumption(consumptionWithPeriod)
                setShowEditModal(true)
              }}
              onDeleteConsumption={(consumption, period) => {
                handleDeleteConsumption(consumption, period)
              }}
              onAdjustConsumption={(consumption, period) => {
                setActivePeriod(period)
                setAdjustingConsumption(consumption)
                setShowAdjustModal(true)
              }}
              onLoadConsumptions={loadConsumptions}
              getFactoryFromLine={getFactoryFromLine}
              sapConsumptionService={sapConsumptionService}
            />
          </>
        )}

        {/* Edit Consumption Modal */}
        {showEditModal && editingConsumption && (
          <EditConsumptionModal
            consumption={editingConsumption}
            selectedOrder={selectedOrder}
            recipe={recipe}
            onClose={() => {
              debug.log('🔴 [MaterialesConsumos] Cerrando modal de edición')
              setShowEditModal(false)
              setEditingConsumption(null)
              clearError()
            }}
            onError={showError}
            onSubmit={async (formData, originalConsumption) => {
              debug.log('🟢 [MaterialesConsumos] onSubmit del modal llamado', { formData, originalConsumption, editingConsumption })
              // Extraer el período del consumo si está disponible (guardado en _period)
              const periodOverride = originalConsumption?._period || editingConsumption?._period || null
              debug.log('📋 [MaterialesConsumos] Período override:', periodOverride)
              return await handleEditConsumption(formData, originalConsumption, periodOverride)
            }}
            isSubmitting={addingConsumption}
          />
        )}

        {/* Edit Period End Modal */}
        {showEditPeriodEndModal && editingPeriod && (
          <EditPeriodEndModal
            period={editingPeriod}
            onClose={() => {
              setShowEditPeriodEndModal(false)
              setEditingPeriod(null)
              clearError()
            }}
            onError={showError}
            onSubmit={async (formData) => {
              return await handleEditPeriodEnd(formData, editingPeriod)
            }}
            isSubmitting={savingPeriodDates}
          />
        )}

        {/* Dosifier Manager Modal */}
        <DosifierManagerModal
          isOpen={showDosifierModal}
          onClose={() => setShowDosifierModal(false)}
          dosifiers={dosifiers}
          onAdd={handleAddDosifier}
          onEdit={handleEditDosifier}
          onDelete={handleDeleteDosifier}
          isSubmitting={managingCRUD}
        />

        {/* Hopper Manager Modal */}
        <HopperManagerModal
          isOpen={showHopperModal}
          onClose={() => setShowHopperModal(false)}
          hoppers={hoppers}
          dosifiers={dosifiers}
          onAdd={handleAddHopper}
          onEdit={handleEditHopper}
          onDelete={handleDeleteHopper}
          isSubmitting={managingCRUD}
        />

        {/* Component Manager Modal */}
        <ComponentManagerModal
          isOpen={showComponentModal}
          onClose={() => setShowComponentModal(false)}
          components={components}
          onAdd={handleAddComponent}
          onEdit={handleEditComponent}
          onDelete={handleDeleteComponent}
          isSubmitting={managingCRUD}
        />

        {/* Adjust Quantity Modal */}
        {showAdjustModal && adjustingConsumption && activePeriod && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Ajustar Cantidad</h3>
                  <button
                    onClick={() => {
                      setShowAdjustModal(false)
                      setAdjustingConsumption(null)
                      clearError()
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Componente</p>
                    <p className="text-white font-mono">{adjustingConsumption.ComponentSapCode || adjustingConsumption.componentSapCode || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Dosificador / Tolva</p>
                    <p className="text-white">
                      {adjustingConsumption.DosingUnit || adjustingConsumption.dosingUnit || '-'} / {adjustingConsumption.DosingHopper || adjustingConsumption.hopper || adjustingConsumption.Hopper || '-'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Cantidad Calculada</p>
                    <p className="text-blue-400 font-mono">
                      {(adjustingConsumption.CalculatedQuantity || adjustingConsumption.calculatedQuantity || 0).toFixed(3)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nueva Cantidad Ajustada <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      defaultValue={adjustingConsumption.AdjustedQuantity || adjustingConsumption.adjustedQuantity || adjustingConsumption.CalculatedQuantity || adjustingConsumption.calculatedQuantity || 0}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      id="adjustedQuantityInput"
                    />
                  </div>
                  
                  {error && (
                    <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAdjustModal(false)
                      setAdjustingConsumption(null)
                      clearError()
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-white text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      const input = document.getElementById('adjustedQuantityInput')
                      const newQuantity = parseFloat(input?.value || 0)
                      if (isNaN(newQuantity) || newQuantity < 0) {
                        showError('La cantidad debe ser un número válido mayor o igual a 0')
                        return
                      }
                      handleAdjustConsumption(adjustingConsumption, newQuantity)
                    }}
                    disabled={addingConsumption}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm flex items-center gap-2"
                  >
                    {addingConsumption ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Ajustando...</span>
                      </>
                    ) : (
                      <>
                        <Settings className="w-4 h-4" />
                        <span>Ajustar</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal para editar Inicio OF */}
        {showStartModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <PlayCircle className="w-5 h-5 text-green-500" />
                    Inicio de OF
                  </h3>
                  <button
                    onClick={() => setShowStartModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Fecha y Hora de Inicio
                  </label>
                  <input
                    type="datetime-local"
                    value={tempStartDateTime}
                    onChange={(e) => setTempStartDateTime(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowStartModal(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveStartDateTime}
                    disabled={savingStartDateTime}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white transition-colors flex items-center gap-2"
                  >
                    {savingStartDateTime ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Guardando...</span>
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Modal para editar Fin OF */}
        {showEndModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Square className="w-5 h-5 text-red-500" />
                    Fin de OF
                  </h3>
                  <button
                    onClick={() => setShowEndModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Fecha y Hora de Fin
                  </label>
                  <input
                    type="datetime-local"
                    value={tempEndDateTime}
                    onChange={(e) => setTempEndDateTime(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowEndModal(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveEndDateTime}
                    disabled={savingEndDateTime}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-white transition-colors flex items-center gap-2"
                  >
                    {savingEndDateTime ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Finalizando...
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default MaterialsConsumablesPage // Componente ya migrado desde MaterialsConsumablesPage


